import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiRefreshCw, FiEdit2, FiSave, FiCheckCircle, FiInfo, FiX, FiPlus, FiEye, FiTrash2, FiCheck, FiShoppingCart, FiTruck, FiDownload, FiTrendingUp, FiTrendingDown, FiColumns } from 'react-icons/fi';
import api from '../services/api';
import Modal from '../components/Modal';
import InkooporderBetaalinfoCard from '../components/InkooporderBetaalinfoCard';
import { useSearch } from '../contexts/SearchContext';
import '../components/Button.css';
import './Inkoopvoorstellen.css';
import { ProductNaamMetKlasse, PRODUCT_KLASSE_META } from '../components/ProductKlasseIcon';
import ProductKlasseIcon from '../components/ProductKlasseIcon';
import { useProductKlasse } from '../contexts/ProductKlasseContext';

const defaultCompanyInfo = {
  name: 'Company Name',
  address: '',
  email: '',
  phone: '',
  website: ''
};

const MAX_RANKING_COMPARE = 6;

function rankingCompareCellClass(values, idx, higherIsBetter = true) {
  const nums = values.map((v) => (v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v)));
  const valid = nums.filter((n) => n != null);
  if (valid.length < 2 || nums[idx] == null) return '';
  const best = higherIsBetter ? Math.max(...valid) : Math.min(...valid);
  const worst = higherIsBetter ? Math.min(...valid) : Math.max(...valid);
  if (nums[idx] === best && best !== worst) return 'product-ranking-compare-best';
  if (nums[idx] === worst && best !== worst) return 'product-ranking-compare-worst';
  return '';
}

const Inkoopvoorstellen = () => {
  const [proposals, setProposals] = useState(null);
  const [savedProposals, setSavedProposals] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProposals, setSelectedProposals] = useState(new Set());
  const [uitverkoopData, setUitverkoopData] = useState(null);
  const [loadingUitverkoop, setLoadingUitverkoop] = useState(false);
  const [showUitverkoop, setShowUitverkoop] = useState(false);
  const [productRankingData, setProductRankingData] = useState(null);
  const [loadingProductRanking, setLoadingProductRanking] = useState(false);
  const [showProductRanking, setShowProductRanking] = useState(false);
  const [rankingFilters, setRankingFilters] = useState({
    limit: 10,
    categorien: [],
    lijst: 'beide',
    weergave: 'gecombineerd',
    klasse: '',
    leverancier_id: '',
  });
  const [rankingUitfaserenBusy, setRankingUitfaserenBusy] = useState({});
  const [rankingUitfaserenLocal, setRankingUitfaserenLocal] = useState({});
  const [rankingCompareItems, setRankingCompareItems] = useState([]);
  const [rankingCompareQuery, setRankingCompareQuery] = useState('');
  const [rankingCompareData, setRankingCompareData] = useState(null);
  const [rankingCompareLoading, setRankingCompareLoading] = useState(false);
  const [uitverkoopSortColumn, setUitverkoopSortColumn] = useState(null);
  const [uitverkoopSortDirection, setUitverkoopSortDirection] = useState('asc');
  const [uitverkoopColumnWidths, setUitverkoopColumnWidths] = useState([140, 70, 95, 48, 48, 48, 42, 48, 52, 52, 58, 78, 85, 120]);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [calculationData, setCalculationData] = useState(null);
  const [calculationProductId, setCalculationProductId] = useState(null);
  const [calculationFromUitverkoop, setCalculationFromUitverkoop] = useState(false);
  const [loadingCalculation, setLoadingCalculation] = useState(false);
  const [correctingInProductie, setCorrectingInProductie] = useState(false);
  const [leveranciers, setLeveranciers] = useState([]);
  const [selectedLeverancier, setSelectedLeverancier] = useState('');
  const { searchTerm } = useSearch();
  const navigate = useNavigate();
  const { refresh: refreshProductKlasse } = useProductKlasse();
  const [searchParams] = useSearchParams();
  
  // Inkooporders state
  const [inkooporders, setInkooporders] = useState([]);
  const [producten, setProducten] = useState([]);
  const [productSuggestions, setProductSuggestions] = useState({});
  const [showInkooporders, setShowInkooporders] = useState(false);
  const [showInkooporderModal, setShowInkooporderModal] = useState(false);
  const [showInkooporderDetailModal, setShowInkooporderDetailModal] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailForm, setDetailForm] = useState(null);
  const [levertijdenInline, setLevertijdenInline] = useState({ verwachte_leverdatum: '', ata_tijd: '' });
  const [levertijdenSaving, setLevertijdenSaving] = useState(false);
  const [collapsedSuppliers, setCollapsedSuppliers] = useState({});
  const [companyInfo, setCompanyInfo] = useState(defaultCompanyInfo);
  // Kolom-breedtes in pixels voor resizable columns (indices: 0=Product,1=SKU,2=Voorraad,...,10=CBM/st,11=CBM tot,12=Acties)
  const [colWidths, setColWidths] = useState([160, 80, 55, 60, 45, 55, 45, 75, 50, 60, 48, 52, 70]);
  const resizingCol = React.useRef(null);
  const [manualAddForms, setManualAddForms] = useState({});
  const [editingOrdernummer, setEditingOrdernummer] = useState(null); // { orderId: value }
  // Geaccordeerde adviesrijen (container vol): { [leverancierId]: { [productId]: aantal } }
  const [accordedAdvies, setAccordedAdvies] = useState({});
  const [inkooporderFormData, setInkooporderFormData] = useState({
    leverancier_id: '',
    verwachte_leverdatum: '',
    ata_tijd: '',
    opmerkingen: '',
    interne_notitie: '',
    regels: []
  });

  useEffect(() => {
    fetchSavedProposals();
    fetchLeveranciers();
    if (showInkooporders) {
      fetchInkooporders();
      fetchProducten();
      fetchProductSuggestions();
    }
    // eslint-disable-next-line
  }, [showInkooporders]);

  // Ensure productSuggestions are loaded when modal opens
  useEffect(() => {
    if (showInkooporderModal && Object.keys(productSuggestions).length === 0) {
      console.log('[Inkooporders] Loading productSuggestions because modal opened');
      fetchProductSuggestions();
      fetchProducten();
    }
    // eslint-disable-next-line
  }, [showInkooporderModal]);

  useEffect(() => {
    fetchCompanyInfo();
    fetchProducten(); // needed for manual add in proposals
  }, []);

  // Filter uitverkoopdata op basis van zoekterm
  const filteredUitverkoopData = useMemo(() => {
    if (!uitverkoopData || !searchTerm || !searchTerm.trim()) {
      return uitverkoopData;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const searchNormalized = searchLower.replace(/\D/g, '');
    
    const filteredProducten = uitverkoopData.producten.filter(product => {
      const productNaam = String(product.product_naam || '').toLowerCase();
      const productSku = String(product.product_sku || '').toLowerCase();
      const leverancierNaam = String(product.leverancier_naam || '').toLowerCase();
      const skuNormalized = productSku.replace(/\D/g, '');
      
      return productNaam.includes(searchLower) || 
             productSku.includes(searchLower) ||
             leverancierNaam.includes(searchLower) ||
             (searchNormalized.length >= 3 && skuNormalized.includes(searchNormalized));
    });

    // Herbereken samenvatting op basis van gefilterde producten
    const samenvatting = {
      kritiek: filteredProducten.filter(p => p.status === 'kritiek').length,
      laag: filteredProducten.filter(p => p.status === 'laag').length,
      waarschuwing: filteredProducten.filter(p => p.status === 'waarschuwing').length,
      voldoende: filteredProducten.filter(p => p.status === 'voldoende').length,
      uitverkocht: filteredProducten.filter(p => p.status === 'uitverkocht').length,
      onder_minimum: filteredProducten.filter(p => p.status === 'onder_minimum').length,
      onvoldoende_data: filteredProducten.filter(p => p.status === 'onvoldoende_data').length
    };

    return {
      ...uitverkoopData,
      producten: filteredProducten,
      samenvatting
    };
  }, [uitverkoopData, searchTerm]);

  // Uitverkoop kolomdefinities: sortKind bepaalt vergelijking (tekst / getallen / datum)
  const uitverkoopColumns = useMemo(() => [
    { key: 'product_naam', label: 'Product', sortKind: 'string', getSortValue: (p) => (p.product_naam || '').toLowerCase() },
    { key: 'product_sku', label: 'SKU', sortKind: 'string', getSortValue: (p) => (p.product_sku || '').toLowerCase() },
    { key: 'leverancier_naam', label: 'Leverancier', sortKind: 'string', getSortValue: (p) => (p.leverancier_naam || '').toLowerCase() },
    { key: 'huidige_voorraad', label: 'Huidige Voorraad', sortKind: 'number', getSortValue: (p) => Number(p.huidige_voorraad) || 0 },
    { key: 'voorraad_onderweg', label: 'Onderweg', sortKind: 'number', getSortValue: (p) => Number(p.voorraad_onderweg) || 0 },
    { key: 'in_productie', label: 'In Productie', sortKind: 'number', getSortValue: (p) => Number(p.in_productie) || 0 },
    { key: 'totaal_voorraad', label: 'Totaal', sortKind: 'number', getSortValue: (p) => Number(p.totaal_voorraad) || 0 },
    { key: 'minimum_voorraad', label: 'Min. Voorraad', sortKind: 'number', getSortValue: (p) => Number(p.minimum_voorraad) || 0 },
    { key: 'dagelijkse_verkoop', label: 'Dagelijkse Verkoop', sortKind: 'number', getSortValue: (p) => Number(p.dagelijkse_verkoop) || 0 },
    { key: 'verwachte_verkoop_per_maand', label: 'Verwachte Verkoop/maand', sortKind: 'number', getSortValue: (p) => Number(p.verwachte_verkoop_per_maand) || 0 },
    { key: 'omloopsnelheid', label: 'Omloopsnelheid (keer per jaar voorraad omzet)', sortKind: 'number', getSortValue: (p) => (p.omloopsnelheid == null || p.omloopsnelheid === '' ? null : Number(p.omloopsnelheid)) },
    { key: 'uitverkoopdatum', label: 'Uitverkoopdatum', sortKind: 'date', getSortValue: (p) => (p.uitverkoopdatum ? new Date(`${p.uitverkoopdatum}T12:00:00`).getTime() : null) },
    { key: 'dagen_tot_bestelmoment', label: 'Dagen tot bestelmoment', sortKind: 'number', getSortValue: (p) => (p.dagen_tot_bestelmoment != null ? Number(p.dagen_tot_bestelmoment) : (p.dagen_tot_uitverkoop != null ? Number(p.dagen_tot_uitverkoop) : -9999)) },
    { key: 'status', label: 'Status', sortKind: 'string', getSortValue: (p) => (p.status || '').toLowerCase() }
  ], []);

  const uitverkoopSortColumnMeta = useMemo(
    () => uitverkoopColumns.find((c) => c.key === uitverkoopSortColumn),
    [uitverkoopColumns, uitverkoopSortColumn]
  );

  const uitverkoopMobileDirLabels = useMemo(() => {
    const k = uitverkoopSortColumnMeta?.sortKind || 'string';
    if (k === 'number') {
      return { asc: 'Oplopend (klein → groot)', desc: 'Aflopend (groot → klein)' };
    }
    if (k === 'date') {
      return { asc: 'Oplopend (vroeg → laat)', desc: 'Aflopend (laat → vroeg)' };
    }
    return { asc: 'Oplopend (A → Z)', desc: 'Aflopend (Z → A)' };
  }, [uitverkoopSortColumnMeta]);

  const sortedUitverkoopProducten = useMemo(() => {
    if (!filteredUitverkoopData?.producten?.length) return [];
    const list = [...filteredUitverkoopData.producten];
    if (!uitverkoopSortColumn) return list;
    const col = uitverkoopColumns.find(c => c.key === uitverkoopSortColumn);
    if (!col) return list;
    const dir = uitverkoopSortDirection === 'asc' ? 1 : -1;
    const kind = col.sortKind || 'string';

    const isEmpty = (v) => {
      if (v == null) return true;
      if (typeof v === 'number' && Number.isNaN(v)) return true;
      if (typeof v === 'string' && v.trim() === '') return true;
      return false;
    };

    list.sort((a, b) => {
      const va = col.getSortValue(a);
      const vb = col.getSortValue(b);
      const emptyA = isEmpty(va);
      const emptyB = isEmpty(vb);
      if (emptyA && emptyB) return 0;
      if (emptyA) return 1;
      if (emptyB) return -1;

      if (kind === 'string') {
        return dir * String(va).localeCompare(String(vb), 'nl', { sensitivity: 'base', numeric: true });
      }
      const na = Number(va);
      const nb = Number(vb);
      if (Number.isNaN(na) || Number.isNaN(nb)) {
        return dir * String(va).localeCompare(String(vb), 'nl', { numeric: true });
      }
      if (na < nb) return -dir;
      if (na > nb) return dir;
      return 0;
    });
    return list;
  }, [filteredUitverkoopData, uitverkoopSortColumn, uitverkoopSortDirection, uitverkoopColumns]);

  const handleUitverkoopSort = useCallback((key) => {
    setUitverkoopSortColumn(prev => {
      if (prev === key) {
        setUitverkoopSortDirection(d => d === 'asc' ? 'desc' : 'asc');
      } else {
        setUitverkoopSortDirection('asc');
      }
      return key;
    });
  }, []);

  const handleUitverkoopResizeStart = useCallback((colIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    let lastX = e.clientX;
    let lastWidth = uitverkoopColumnWidths[colIndex];
    const onMove = (moveE) => {
      const delta = moveE.clientX - lastX;
      lastX = moveE.clientX;
      lastWidth = Math.max(30, lastWidth + delta);
      setUitverkoopColumnWidths(prev => {
        const next = [...prev];
        next[colIndex] = lastWidth;
        return next;
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [uitverkoopColumnWidths]);
  
  const fetchLeveranciers = async () => {
    try {
      const response = await api.get('/leveranciers');
      setLeveranciers(response.data);
    } catch (error) {
      console.error('Error fetching leveranciers:', error);
    }
  };

  const fetchSavedProposals = async () => {
    try {
      const response = await api.get('/inkoopvoorstellen?status=concept');
      setSavedProposals(response.data);
    } catch (error) {
      console.error('Error fetching saved proposals:', error);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await api.get('/parameters');
      const paramMap = {};
      response.data.forEach((param) => {
        paramMap[param.parameter_naam] = param.waarde;
      });
      setCompanyInfo({
        name: paramMap.company_name || defaultCompanyInfo.name,
        address: paramMap.company_address || defaultCompanyInfo.address,
        email: paramMap.company_email || defaultCompanyInfo.email,
        phone: paramMap.company_phone || defaultCompanyInfo.phone,
        website: paramMap.company_website || defaultCompanyInfo.website
      });
    } catch (error) {
      console.error('Error fetching company parameters:', error);
    }
  };

  const handleShowCalculation = async (productId) => {
    if (!productId) {
      alert('Product ID niet gevonden');
      return;
    }
    setCalculationProductId(productId);
    setCalculationFromUitverkoop(false);
    setLoadingCalculation(true);
    setShowCalculationModal(true);
    try {
      const response = await api.get(`/inkoopvoorstellen/debug-product/${productId}`);
      setCalculationData(response.data);
    } catch (error) {
      console.error('Error fetching calculation:', error);
      alert('Fout bij ophalen berekening: ' + (error.response?.data?.error || error.message));
      setShowCalculationModal(false);
    } finally {
      setLoadingCalculation(false);
    }
  };

  const handleCalculate = async (leverancierIdOverride = null) => {
    const levId = leverancierIdOverride !== undefined ? leverancierIdOverride : selectedLeverancier;
    setSelectedLeverancier(levId || '');
    setCalculating(true);
    try {
      console.log('[Frontend] Berekenen voorstellen voor leverancier:', levId || 'alle');
      const response = await api.post('/inkoopvoorstellen/calculate', {
        leverancier_id: levId || null
      });
      console.log('[Frontend] Response ontvangen:', response.data);
      
      if (!response.data) {
        throw new Error('Geen data ontvangen van server');
      }
      
      // Toon netjes in de pagina — geen popup alert; reset geaccordeerde advies
      setProposals(response.data);
      setAccordedAdvies({});
    } catch (error) {
      console.error('[Frontend] Error calculating proposals:', error);
      console.error('[Frontend] Error response:', error.response?.data);
      alert('Fout bij berekenen: ' + (error.response?.data?.error || error.message));
      setProposals(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleSave = async () => {
    if (!proposals) {
      alert('Bereken eerst een voorstel!');
      return;
    }

    setSaving(true);
    try {
      // Geaccordeerde adviesregels meenemen als extra voorstelregels
      const payloadProposals = {
        ...proposals,
        per_leverancier: (proposals.per_leverancier || []).map(lev => {
          const accordedForLev = accordedAdvies[lev.leverancier_id] || {};
          const extraProducten = (lev.advies_producten || [])
            .filter(adv => (accordedForLev[adv.product_id] ?? 0) > 0)
            .map(adv => ({
              leverancier_id: adv.leverancier_id,
              product_id: adv.product_id,
              product_naam: adv.product_naam,
              product_sku: adv.product_sku,
              voorgesteld_aantal: accordedForLev[adv.product_id],
              aangepast_aantal: accordedForLev[adv.product_id],
              minimum_voorraad: adv.minimum_voorraad,
              huidige_voorraad: adv.huidige_voorraad,
              verwachte_verkoop: adv.verwachte_verkoop,
              seizoensfactor: adv.seizoensfactor,
              levertijd_dagen: adv.levertijd_dagen
            }));
          return { ...lev, producten: [...(lev.producten || []), ...extraProducten] };
        })
      };
      await api.post('/inkoopvoorstellen/save', { proposals: payloadProposals, status: 'concept' });
      let createdOrders = 0;
      try {
        const convertRes = await api.post('/inkoopvoorstellen/convert-to-inkooporders');
        createdOrders = convertRes.data?.created ?? 0;
        if (createdOrders > 0) {
          await fetchInkooporders();
          setShowInkooporders(true);
        }
      } catch (convertErr) {
        console.warn('Convert to inkooporders:', convertErr);
      }
      setProposals(null);
      await fetchSavedProposals();
      if (createdOrders > 0) {
        alert(`Voorstellen opgeslagen en ${createdOrders} inkooporder(s) aangemaakt. Bekijk ze onder het tabblad Inkoop orders.`);
      } else {
        alert('Voorstellen opgeslagen!');
      }
    } catch (error) {
      console.error('Error saving proposals:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (proposal) => {
    setEditingProposal(proposal);
    setShowEditModal(true);
  };

  const handleUpdateProposal = async (proposalId, updates) => {
    try {
      await api.put(`/inkoopvoorstellen/${proposalId}`, updates);
      setShowEditModal(false);
      setEditingProposal(null);
      fetchSavedProposals();
      if (proposals) {
        // Update ook in calculated proposals
        const updated = proposals.per_leverancier.map(leverancier => ({
          ...leverancier,
          producten: leverancier.producten.map(p => 
            p.id === proposalId ? { ...p, ...updates } : p
          )
        }));
        setProposals({ ...proposals, per_leverancier: updated });
      }
    } catch (error) {
      console.error('Error updating proposal:', error);
      alert('Fout bij bijwerken: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    if (!proposalId) return;
    if (!window.confirm('Weet je zeker dat je dit voorstel wilt verwijderen?')) return;
    try {
      await api.delete(`/inkoopvoorstellen/${proposalId}`);
      fetchSavedProposals();
    } catch (error) {
      console.error('Error deleting proposal:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  /** Verwijder het hele ordervoorstel (alle regels) van één leverancier - zichtbaar op de kaart */
  const handleDeleteLeverancierProposals = async (leverancier) => {
    const ids = (leverancier.producten || []).map(p => p.id).filter(Boolean);
    if (ids.length === 0) return;
    if (!window.confirm(`Weet je zeker dat je het hele voorstel "${leverancier.leverancier_naam}" (${ids.length} regel(s)) wilt verwijderen?`)) return;
    try {
      for (const id of ids) {
        await api.delete(`/inkoopvoorstellen/${id}`);
      }
      fetchSavedProposals();
    } catch (error) {
      console.error('Error deleting leverancier proposals:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  /** Zet alle opgeslagen (concept) voorstellen om naar inkooporders, zodat ze in Inkoop orders verschijnen */
  const handleConvertSavedToInkooporders = async () => {
    try {
      const res = await api.post('/inkoopvoorstellen/convert-to-inkooporders');
      const created = res.data?.created ?? 0;
      if (created > 0) {
        await fetchInkooporders();
        setShowInkooporders(true);
        fetchSavedProposals();
        alert(`${created} inkooporder(s) aangemaakt. Bekijk ze onder het tabblad Inkoop orders.`);
      } else {
        alert('Er zijn geen opgeslagen voorstellen om om te zetten, of ze zijn al omgezet.');
      }
    } catch (error) {
      console.error('Error converting saved to inkooporders:', error);
      alert('Fout bij omzetten: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleMarkDefinitief = async (proposalIds) => {
    if (proposalIds.length === 0) {
      alert('Selecteer eerst voorstellen!');
      return;
    }

    if (!window.confirm(`Weet je zeker dat je ${proposalIds.length} voorstel(len) definitief wilt maken?`)) {
      return;
    }

    try {
      await api.post('/inkoopvoorstellen/definitief', { proposalIds });
      alert('Voorstellen gemarkeerd als definitief!');
      fetchSavedProposals();
      setSelectedProposals(new Set());
    } catch (error) {
      console.error('Error marking as definitief:', error);
      alert('Fout: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleShowUitverkoop = async () => {
    setLoadingUitverkoop(true);
    setShowUitverkoop(true);
    setShowProductRanking(false);
    try {
      const response = await api.get('/inkoopvoorstellen/uitverkoop');
      setUitverkoopData(response.data);
    } catch (error) {
      console.error('Error fetching uitverkoop data:', error);
      alert('Fout bij ophalen uitverkoopdata: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingUitverkoop(false);
    }
  };

  const buildProductRankingParams = useCallback((filters) => {
    const params = new URLSearchParams();
    params.set('limit', String(filters.limit || 10));
    if (filters.lijst && filters.lijst !== 'beide') params.set('lijst', filters.lijst);
    if (filters.weergave === 'per_categorie') params.set('weergave', 'per_categorie');
    if (filters.klasse) params.set('klasse', filters.klasse);
    if (filters.leverancier_id) params.set('leverancier_id', String(filters.leverancier_id));
    (filters.categorien || []).forEach((cat) => params.append('categorie', cat));
    return params;
  }, []);

  const fetchProductRanking = useCallback(async (filters = rankingFilters) => {
    setLoadingProductRanking(true);
    try {
      const params = buildProductRankingParams(filters);
      const response = await api.get(`/inkoopvoorstellen/product-ranking?${params.toString()}`);
      setProductRankingData(response.data);
      await refreshProductKlasse();
    } catch (error) {
      console.error('Error fetching product ranking:', error);
      alert('Fout bij ophalen productranking: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingProductRanking(false);
    }
  }, [buildProductRankingParams, rankingFilters, refreshProductKlasse]);

  const handleShowProductRanking = async () => {
    setShowProductRanking(true);
    setShowUitverkoop(false);
    setShowInkooporders(false);
    setSavedProposals(null);
    await fetchProductRanking(rankingFilters);
  };

  const handleApplyRankingFilters = () => {
    fetchProductRanking(rankingFilters);
  };

  const addToRankingCompare = useCallback((item) => {
    const id = item?.product_id ?? item?.id;
    if (!id) return;
    setRankingCompareItems((prev) => {
      if (prev.some((p) => p.id === id)) return prev;
      if (prev.length >= MAX_RANKING_COMPARE) {
        window.alert(`Maximaal ${MAX_RANKING_COMPARE} producten tegelijk vergelijken.`);
        return prev;
      }
      return [
        ...prev,
        {
          id,
          naam: item.naam || `Product #${id}`,
          sku: item.sku || '',
        },
      ];
    });
    setRankingCompareData(null);
  }, []);

  const removeFromRankingCompare = useCallback((id) => {
    setRankingCompareItems((prev) => prev.filter((p) => p.id !== id));
    setRankingCompareData(null);
  }, []);

  const clearRankingCompare = useCallback(() => {
    setRankingCompareItems([]);
    setRankingCompareQuery('');
    setRankingCompareData(null);
  }, []);

  const runRankingCompare = async () => {
    if (rankingCompareItems.length < 2) {
      window.alert('Selecteer minimaal 2 producten om te vergelijken.');
      return;
    }
    setRankingCompareLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('ids', rankingCompareItems.map((p) => p.id).join(','));
      if (rankingFilters.leverancier_id) {
        params.set('leverancier_id', String(rankingFilters.leverancier_id));
      }
      const response = await api.get(`/inkoopvoorstellen/product-ranking/compare?${params.toString()}`);
      setRankingCompareData(response.data);
      if (response.data?.niet_gevonden?.length) {
        window.alert(
          `Niet in ranking opgenomen (uitgefaseerd of onbekend): ${response.data.niet_gevonden.join(', ')}`
        );
      }
    } catch (error) {
      window.alert(error.response?.data?.error || error.message || 'Vergelijken mislukt');
    } finally {
      setRankingCompareLoading(false);
    }
  };

  const rankingCompareSuggestions = useMemo(() => {
    const q = rankingCompareQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    const selected = new Set(rankingCompareItems.map((p) => p.id));
    return (producten || [])
      .filter((p) => p?.id && !selected.has(p.id))
      .filter((p) => {
        const naam = String(p.naam || '').toLowerCase();
        const sku = String(p.sku || '').toLowerCase();
        return naam.includes(q) || sku.includes(q);
      })
      .slice(0, 8);
  }, [rankingCompareQuery, producten, rankingCompareItems]);

  const toggleRankingCategory = (catNaam) => {
    setRankingFilters((prev) => {
      const selected = new Set(prev.categorien || []);
      if (selected.has(catNaam)) selected.delete(catNaam);
      else selected.add(catNaam);
      return { ...prev, categorien: [...selected] };
    });
  };

  const clearRankingCategories = () => {
    setRankingFilters((prev) => ({ ...prev, categorien: [] }));
  };

  const removeProductFromRankingLists = useCallback((productId) => {
    setProductRankingData((prev) => {
      if (!prev) return prev;
      const strip = (arr) => (arr || []).filter((p) => p.product_id !== productId);
      const next = {
        ...prev,
        top_producten: strip(prev.top_producten),
        uit_te_faseren: strip(prev.uit_te_faseren),
      };
      if (prev.lijsten_per_categorie) {
        next.lijsten_per_categorie = Object.fromEntries(
          Object.entries(prev.lijsten_per_categorie).map(([cat, lists]) => [
            cat,
            {
              top_producten: strip(lists.top_producten),
              uit_te_faseren: strip(lists.uit_te_faseren),
            },
          ])
        );
      }
      return next;
    });
  }, []);

  const handleRankingUitfaserenToggle = async (productId, checked) => {
    if (rankingUitfaserenBusy[productId]) return;

    setRankingUitfaserenBusy((prev) => ({ ...prev, [productId]: true }));
    setRankingUitfaserenLocal((prev) => ({ ...prev, [productId]: checked }));

    try {
      await api.patch(`/producten/${productId}/uitfaseren`, { uitfaseren: checked ? 1 : 0 });
      if (checked) {
        removeProductFromRankingLists(productId);
      } else {
        await fetchProductRanking(rankingFilters);
      }
      setRankingUitfaserenLocal((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      refreshProductKlasse();
    } catch (error) {
      console.error('Error updating uitfaseren:', error);
      setRankingUitfaserenLocal((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
      alert('Fout bij uitfaseren: ' + (error.response?.data?.error || error.message));
    } finally {
      setRankingUitfaserenBusy((prev) => {
        const next = { ...prev };
        delete next[productId];
        return next;
      });
    }
  };

  // Open tab vanaf URL ?tab=inkooporders, uitverkoop of product-ranking
  const tabParam = searchParams.get('tab');
  useEffect(() => {
    if (tabParam === 'inkooporders') {
      setShowInkooporders(true);
      setShowUitverkoop(false);
      setShowProductRanking(false);
      setSavedProposals(null);
      fetchInkooporders();
      fetchProducten();
      fetchProductSuggestions();
    } else if (tabParam === 'uitverkoop') {
      setShowInkooporders(false);
      setShowProductRanking(false);
      setSavedProposals(null);
      handleShowUitverkoop();
    } else if (tabParam === 'product-ranking') {
      handleShowProductRanking();
    }
    // eslint-disable-next-line
  }, [tabParam]);

  const handleShowUitverkoopCalculation = async (productId) => {
    setCalculationProductId(productId);
    setCalculationFromUitverkoop(true);
    setLoadingCalculation(true);
    setShowCalculationModal(true);
    try {
      const response = await api.get(`/inkoopvoorstellen/uitverkoop/debug/${productId}`);
      setCalculationData(response.data);
    } catch (error) {
      console.error('Error fetching uitverkoop calculation:', error);
      alert('Fout bij ophalen berekening: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingCalculation(false);
    }
  };

  const handleZetInProductieOpNul = async () => {
    const productId = calculationData?.product?.id ?? calculationProductId;
    if (!productId) return;
    if (!window.confirm('Zet "In productie" voor dit product op 0? (Correctie als er feitelijk niets in productie staat.)')) return;
    setCorrectingInProductie(true);
    try {
      await api.patch(`/producten/${productId}/in-productie`, { in_productie: 0 });
      if (calculationFromUitverkoop) {
        const response = await api.get(`/inkoopvoorstellen/uitverkoop/debug/${productId}`);
        setCalculationData(response.data);
      } else {
        const response = await api.get(`/inkoopvoorstellen/debug-product/${productId}`);
        setCalculationData(response.data);
      }
    } catch (err) {
      alert('Fout: ' + (err.response?.data?.error || err.message));
    } finally {
      setCorrectingInProductie(false);
    }
  };

  const handleConvertToInkooporders = async () => {
    const definitiefPerLeverancier = savedProposals?.per_leverancier?.map(lev => ({
      ...lev,
      producten: lev.producten.filter(p => p.status === 'definitief')
    })).filter(lev => lev.producten.length > 0) || [];

    if (definitiefPerLeverancier.length === 0) {
      alert('Geen definitieve voorstellen gevonden. Markeer eerst voorstellen als definitief!');
      return;
    }

    const totalItems = definitiefPerLeverancier.reduce((sum, lev) => sum + lev.producten.length, 0);
    if (!window.confirm(`Zeker dat je ${totalItems} definitieve voorstel(len) wilt omzetten naar inkooporders?`)) {
      return;
    }

    try {
      let created = 0;
      for (const lev of definitiefPerLeverancier) {
        const regels = lev.producten.map(p => ({
          product_id: p.product_id || p.id,
          aantal: p.aangepast_aantal !== null && p.aangepast_aantal !== undefined ? p.aangepast_aantal : p.voorgesteld_aantal || 0,
          eenheidsprijs: p.inkoopprijs ?? 0
        })).filter(r => r.product_id && r.aantal > 0);

        if (regels.length === 0) {
          continue;
        }

        await api.post('/inkooporders', {
          leverancier_id: lev.leverancier_id,
          verwachte_leverdatum: null,
          ata_tijd: null,
          opmerkingen: 'Geconverteerd vanuit inkoopvoorstel (definitief)',
          regels
        });
        created += 1;
      }

      alert(`Succes! ${created} inkooporder(s) aangemaakt.`);
      fetchSavedProposals();
      fetchInkooporders();
      setShowInkooporders(true);
      setSavedProposals(null);
    } catch (error) {
      console.error('Error converting to inkooporders:', error);
      alert('Fout bij omzetten: ' + (error.response?.data?.error || error.message));
    }
  };

  const toggleProposalSelection = (proposalId) => {
    const newSelected = new Set(selectedProposals);
    if (newSelected.has(proposalId)) {
      newSelected.delete(proposalId);
    } else {
      newSelected.add(proposalId);
    }
    setSelectedProposals(newSelected);
  };

  // Inkooporders functies
  const fetchInkooporders = async () => {
    try {
      const response = await api.get('/inkooporders');
      setInkooporders(response.data);
    } catch (error) {
      console.error('Error fetching inkooporders:', error);
    }
  };

  const fetchProducten = async () => {
    try {
      const response = await api.get('/producten');
      setProducten(response.data);
    } catch (error) {
      console.error('Error fetching producten:', error);
    }
  };

  const handleManualAddChange = (leverancierId, field, value) => {
    setManualAddForms(prev => ({
      ...prev,
      [leverancierId]: {
        ...(prev[leverancierId] || { productId: '', aantal: 1 }),
        [field]: value
      }
    }));
  };

  const handleAddProductToProposal = async (leverancierId, isSaved = false) => {
    const form = manualAddForms[leverancierId] || {};
    const productId = parseInt(form.productId, 10);
    const aantal = parseInt(form.aantal, 10) || 0;

    if (!productId) {
      alert('Kies een product om toe te voegen.');
      return;
    }
    if (aantal <= 0) {
      alert('Aantal moet groter zijn dan 0.');
      return;
    }

    const product = producten.find(p => p.id === productId);
    if (!product) {
      alert('Product niet gevonden.');
      return;
    }
    if (product.leverancier_id && Number(product.leverancier_id) !== Number(leverancierId)) {
      alert('Dit product hoort bij een andere leverancier.');
      return;
    }

    try {
      if (isSaved) {
        await api.post(`/inkoopvoorstellen/${leverancierId}/add`, {
          product_id: productId,
          aantal
        });
        const refreshed = await api.get('/inkoopvoorstellen?status=concept');
        setSavedProposals(refreshed.data);
      } else {
        const targetState = proposals || { per_leverancier: [] };
        const updated = { ...targetState, per_leverancier: [...(targetState.per_leverancier || [])] };
        let leverancier = updated.per_leverancier.find(l => l.leverancier_id === leverancierId);

        if (!leverancier) {
          leverancier = {
            leverancier_id: leverancierId,
            leverancier_naam: product.leverancier_naam || 'Leverancier',
            producten: []
          };
          updated.per_leverancier.push(leverancier);
        }

        const existingIndex = leverancier.producten.findIndex(p => (p.product_id || p.id) === productId);
        const newEntry = {
          id: product.id,
          product_id: product.id,
          leverancier_id: leverancierId,
          leverancier_naam: leverancier.leverancier_naam,
          product_naam: product.naam,
          product_sku: product.sku,
          huidige_voorraad: product.voorraad || 0,
          voorraad_onderweg: product.voorraad_onderweg || 0,
          in_productie: product.in_productie || 0,
          minimum_voorraad: product.minimum_voorraad || 0,
          voorgesteld_aantal: aantal,
          aangepast_aantal: aantal,
          cbm_per_stuk: product.cbm_per_stuk || 0,
          status: 'concept'
        };

        if (existingIndex >= 0) {
          leverancier.producten[existingIndex] = { ...leverancier.producten[existingIndex], ...newEntry };
        } else {
          leverancier.producten.push(newEntry);
        }
        setProposals(updated);
      }
    } catch (error) {
      console.error('Error adding product to proposal:', error);
      alert('Fout bij toevoegen product: ' + (error.response?.data?.error || error.message));
    } finally {
      setManualAddForms(prev => ({
        ...prev,
        [leverancierId]: { productId: '', aantal: 1 }
      }));
    }
  };

  const normalizeEAN = (ean) => {
    if (!ean) return '';
    return String(ean).trim().replace(/\D/g, '');
  };

  const fetchProductSuggestions = async () => {
    try {
      const response = await api.get('/producten');
      const suggestions = {};
      response.data.forEach(product => {
        // Check both EAN and SKU columns
        const eanValue = product.ean || product.sku;
        if (eanValue) {
          const normalized = normalizeEAN(eanValue);
          if (normalized) {
            const productData = {
              naam: product.naam,
              prijs: product.inkoopprijs ?? 0,
              inkoopprijs: product.inkoopprijs ?? null,
              id: product.id,
              ean: product.ean,
              sku: product.sku
            };
            // Store with normalized EAN
            suggestions[normalized] = productData;
            // Also store with original value for backwards compatibility
            if (eanValue !== normalized) {
              suggestions[eanValue] = productData;
            }
            // Also store with original EAN/SKU if different
            if (product.ean && product.ean !== normalized && product.ean !== eanValue) {
              suggestions[product.ean] = productData;
            }
            if (product.sku && product.sku !== normalized && product.sku !== eanValue) {
              suggestions[product.sku] = productData;
            }
          }
        }
      });
      console.log(`[Inkooporders] Loaded ${Object.keys(suggestions).length} product suggestions`);
      setProductSuggestions(suggestions);
    } catch (error) {
      console.error('Error fetching product suggestions:', error);
    }
  };

  const handleInkooporderSubmit = async (e) => {
    e.preventDefault();
    if (inkooporderFormData.regels.length === 0) {
      alert('Voeg minimaal één product toe aan de order');
      return;
    }

    try {
      if (editingOrderId) {
        // Update existing order
        await api.put(`/inkooporders/${editingOrderId}`, inkooporderFormData);
        alert('Order bijgewerkt!');
      } else {
        // Create new order
        await api.post('/inkooporders', inkooporderFormData);
        alert('Order aangemaakt!');
      }
      setShowInkooporderModal(false);
      resetInkooporderForm();
      setEditingOrderId(null);
      fetchInkooporders();
    } catch (error) {
      console.error('Error saving inkooporder:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditOrder = async (id) => {
    try {
      const response = await api.get(`/inkooporders/${id}`);
      const order = response.data;
      
      // Convert order regels to form format
      const regels = order.regels.map(regel => ({
        ean: regel.ean || regel.sku || '',
        product_id: regel.product_id,
        aantal: regel.aantal,
        eenheidsprijs: regel.eenheidsprijs,
        product_naam: regel.product_naam
      }));

      setInkooporderFormData({
        leverancier_id: order.leverancier_id,
        verwachte_leverdatum: order.verwachte_leverdatum ? order.verwachte_leverdatum.split('T')[0] : '',
        ata_tijd: order.ata_tijd ? new Date(order.ata_tijd).toISOString().slice(0, 16) : '',
        opmerkingen: order.opmerkingen || '',
        interne_notitie: order.interne_notitie || '',
        regels: regels
      });
      
      setEditingOrderId(id);
      setShowInkooporderModal(true);
    } catch (error) {
      console.error('Error fetching order for edit:', error);
      alert('Fout bij ophalen order: ' + (error.response?.data?.error || error.message));
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    const s = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    return s.split('T')[0] || '';
  };

  const formatDateTimeLocalForInput = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  };

  const handleViewOrderDetail = async (id) => {
    try {
      const response = await api.get(`/inkooporders/${id}`);
      const data = response.data;
      setSelectedOrder(data);
      setDetailForm({
        ordernummer: data.ordernummer || '',
        leverancier_id: data.leverancier_id || '',
        verwachte_leverdatum: formatDateForInput(data.verwachte_leverdatum),
        ata_tijd: formatDateTimeLocalForInput(data.ata_tijd),
        opmerkingen: data.opmerkingen || '',
        interne_notitie: data.interne_notitie || '',
        status: data.status || 'concept',
        regels: (data.regels || []).map(r => {
          const ep = parseFloat(r.eenheidsprijs);
          const verkoop = parseFloat(r.product_prijs);
          const isVerkoopprijs = r.product_prijs != null && !isNaN(verkoop) && Math.abs(ep - verkoop) < 0.001;
          const eenheidsprijs = isVerkoopprijs ? (parseFloat(r.product_inkoopprijs) || 0) : r.eenheidsprijs;
          return { id: r.id, product_id: r.product_id, aantal: r.aantal, eenheidsprijs, ean: r.ean || r.sku || '', product_naam: r.product_naam, product_inkoopprijs: r.product_inkoopprijs, product_prijs: r.product_prijs };
        })
      });
      setLevertijdenInline({
        verwachte_leverdatum: formatDateForInput(data.verwachte_leverdatum),
        ata_tijd: formatDateTimeLocalForInput(data.ata_tijd)
      });
      setDetailEditMode(false);
      setShowInkooporderDetailModal(true);
    } catch (error) {
      console.error('Error fetching order detail:', error);
    }
  };

  const saveLevertijden = async () => {
    if (!selectedOrder) return;
    setLevertijdenSaving(true);
    try {
      await api.put(`/inkooporders/${selectedOrder.id}/levertijden`, {
        verwachte_leverdatum: levertijdenInline.verwachte_leverdatum || null,
        ata_tijd: levertijdenInline.ata_tijd || null
      });
      const response = await api.get(`/inkooporders/${selectedOrder.id}`);
      setSelectedOrder(response.data);
      fetchInkooporders();
    } catch (error) {
      console.error('Error saving levertijden:', error);
      alert('Fout bij opslaan levertijden: ' + (error.response?.data?.error || error.message));
    } finally {
      setLevertijdenSaving(false);
    }
  };

  const formatCurrency = (value, valuta = 'EUR') => {
    const number = Number(value || 0);
    const symbol = valuta === 'USD' ? '$' : '€';
    return `${symbol} ${number.toFixed(2)}`;
  };

  const getRegelEffectivePrice = (regel) => {
    // Nooit verkoopprijs tonen: als eenheidsprijs gelijk is aan product_prijs (verkoopprijs), gebruik inkoopprijs of 0
    const ep = parseFloat(regel.product_inkoopprijs ?? regel.eenheidsprijs ?? 0) || 0;
    const verkoop = parseFloat(regel.product_prijs);
    if (regel.product_prijs != null && !isNaN(verkoop) && Math.abs(ep - verkoop) < 0.001) {
      return parseFloat(regel.product_inkoopprijs) || 0;
    }
    return ep;
  };

  const formatDateNL = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('nl-NL');
  };

  const formatDateNl = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('nl-NL');
  };

  const loadJsPdfLibrary = () => {
    return new Promise((resolve, reject) => {
      if (window.jspdf?.jsPDF) {
        resolve(window.jspdf.jsPDF);
        return;
      }

      const existingScript = document.getElementById('jspdf-cdn');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.jspdf.jsPDF));
        existingScript.addEventListener('error', () => reject(new Error('jsPDF load failed')));
        return;
      }

      const script = document.createElement('script');
      script.id = 'jspdf-cdn';
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload = () => resolve(window.jspdf.jsPDF);
      script.onerror = () => reject(new Error('jsPDF load failed'));
      document.body.appendChild(script);
    });
  };

  const fetchLogoDataUrl = async () => {
    try {
      const response = await fetch('/logo.png');
      if (!response.ok) return null;
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Logo niet gevonden voor PDF, sla over.', error);
      return null;
    }
  };

  const handleDownloadOrderPdf = async (orderOrId) => {
    if (!orderOrId) return;
    try {
      // Zorg dat we volledige orderdetails hebben (incl. regels)
      let order = typeof orderOrId === 'object' ? orderOrId : null;
      const orderId = order?.id || orderOrId;

      if (!order?.regels || !Array.isArray(order.regels) || order.regels.length === 0) {
        const response = await api.get(`/inkooporders/${orderId}`);
        order = response.data;
      }

      const jsPDFConstructor = await loadJsPdfLibrary();
      const doc = new jsPDFConstructor();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let totalCbm = 0;

      const logoDataUrl = await fetchLogoDataUrl();
      if (logoDataUrl) {
        // Behoud aspect ratio van logo (standaard 2:1 ratio, pas aan als nodig)
        // Voor een 4SHINE logo, gebruik een redelijke breedte en bereken hoogte
        const logoWidth = 45;
        const logoAspectRatio = 2.5; // Pas dit aan op basis van je logo (breedte/hoogte)
        const logoHeight = logoWidth / logoAspectRatio;
        doc.addImage(logoDataUrl, 'PNG', 14, 12, logoWidth, logoHeight, undefined, 'FAST');
      }

      doc.setFontSize(16).text('Purchase Order', pageWidth - 14, 18, { align: 'right' });
      doc.setFontSize(10);
      doc.text(companyInfo.name || 'Company Name', pageWidth - 14, 26, { align: 'right' });
      if (companyInfo.address) doc.text(companyInfo.address, pageWidth - 14, 32, { align: 'right' });
      if (companyInfo.email) doc.text(companyInfo.email, pageWidth - 14, 38, { align: 'right' });
      if (companyInfo.phone) doc.text(companyInfo.phone, pageWidth - 14, 44, { align: 'right' });
      if (companyInfo.website) doc.text(companyInfo.website, pageWidth - 14, 50, { align: 'right' });

      let cursorY = 60;
      doc.setFontSize(12).text('Order Details', 14, cursorY, { underline: true });
      doc.setFontSize(10);
      cursorY += 6;
      doc.text(`Order number: ${order.ordernummer}`, 14, cursorY);
      cursorY += 5;
      doc.text(`Order date: ${formatDateNL(order.datum)}`, 14, cursorY);
      cursorY += 5;
      doc.text(`Expected delivery: ${formatDateNL(order.verwachte_leverdatum)}`, 14, cursorY);
      cursorY += 5;
      doc.text(`ATA time: ${order.ata_tijd ? new Date(order.ata_tijd).toLocaleString('nl-NL') : '-'}`, 14, cursorY);
      cursorY += 5;
      doc.text(`Status: ${order.status || 'open'}`, 14, cursorY);
      cursorY += 7;
      if (order.opmerkingen) {
        const notitieLines = doc.splitTextToSize(`Notitie: ${order.opmerkingen}`, doc.internal.pageSize.getWidth() - 28);
        notitieLines.forEach((line) => { doc.text(line, 14, cursorY); cursorY += 5; });
        cursorY += 2;
      }

      doc.setFontSize(12).text('Supplier', 14, cursorY, { underline: true });
      cursorY += 6;
      doc.setFontSize(10);
      doc.text(order.leverancier_naam || 'Supplier', 14, cursorY);
      cursorY += 5;
      if (order.leverancier_contactpersoon) {
        doc.text(`Contact: ${order.leverancier_contactpersoon}`, 14, cursorY);
        cursorY += 5;
      }
      if (order.leverancier_email) {
        doc.text(`Email: ${order.leverancier_email}`, 14, cursorY);
        cursorY += 5;
      }
      if (order.leverancier_telefoon) {
        doc.text(`Phone: ${order.leverancier_telefoon}`, 14, cursorY);
        cursorY += 5;
      }
      if (order.leverancier_adres) {
        doc.text(`Address: ${order.leverancier_adres}`, 14, cursorY);
        cursorY += 5;
      }

      cursorY += 6;
      // Kolom posities met meer ruimte tussen kolommen - vooral tussen Qty en CBM
      const colProductStart = 14;
      const colProductMaxWidth = 42; // Maximale breedte voor product kolom
      const colSkuStart = 60; // Meer ruimte tussen product en SKU
      const colSkuMaxWidth = 16; // Maximale breedte voor SKU kolom
      const colQtyStart = 88; // Qty kolom - meer naar rechts
      const colCbmStart = 110; // CBM kolom met meer ruimte na Qty (22mm tussenruimte)
      const colUnitPriceStart = 140; // Unit price kolom - meer naar rechts
      const colLineTotalStart = 175; // Line total kolom - meer naar rechts
      
      doc.setFontSize(10); // Iets kleiner voor headers
      doc.text('Product', colProductStart, cursorY);
      doc.text('SKU/EAN', colSkuStart, cursorY);
      // Qty en CBM headers rechts uitlijnen zodat ze overeenkomen met de getallen eronder
      doc.text('Qty', colQtyStart, cursorY, { align: 'right' });
      doc.text('CBM (total)', colCbmStart, cursorY, { align: 'right' });
      doc.text('Inkoopprijs', colUnitPriceStart, cursorY, { align: 'right' });
      doc.text('Line total', colLineTotalStart, cursorY, { align: 'right' });
      doc.line(14, cursorY + 2, pageWidth - 14, cursorY + 2);

      const orderValuta = order.leverancier_valuta || 'EUR';
      const fmtCurr = (v) => formatCurrency(v, orderValuta);

      cursorY += 8;
      doc.setFontSize(9); // Iets kleiner font voor betere fit
      (order.regels || []).forEach((regel) => {
        if (cursorY > pageHeight - 30) {
          doc.addPage();
          cursorY = 20;
        }
        const effPrice = getRegelEffectivePrice(regel);
        const lineTotal = (regel.aantal || 0) * effPrice;
        const cbmPer = regel.cbm_per_stuk || regel.cbm || 0;
        const lineCbm = (regel.aantal || 0) * cbmPer;
        totalCbm += lineCbm;

        // Product naam met tekst wrapping om overlap te voorkomen
        const productName = regel.product_naam || 'Product';
        const productLines = doc.splitTextToSize(productName, colProductMaxWidth);
        const startY = cursorY;
        
        // Tekst product naam met wrapping
        productLines.forEach((line, index) => {
          doc.text(line, colProductStart, cursorY);
          if (index < productLines.length - 1) {
            cursorY += 4; // Regelafstand voor wrapped tekst
          }
        });
        
        // SKU/EAN met tekst wrapping als nodig
        const skuText = regel.sku || regel.ean || '-';
        const skuLines = doc.splitTextToSize(skuText, colSkuMaxWidth);
        skuLines.forEach((line, index) => {
          doc.text(line, colSkuStart, startY + (index * 4));
        });
        
        // Andere kolommen op dezelfde hoogte als eerste regel
        // Qty en CBM moeten duidelijk gescheiden zijn en rechts uitgelijnd
        doc.text(String(regel.aantal || 0), colQtyStart, startY, { align: 'right' });
        doc.text(lineCbm ? lineCbm.toFixed(3) : '-', colCbmStart, startY, { align: 'right' });
        doc.text(fmtCurr(effPrice), colUnitPriceStart, startY, { align: 'right' });
        doc.text(fmtCurr(lineTotal), colLineTotalStart, startY, { align: 'right' });
        
        // Verhoog cursorY met hoogste regel (product naam of SKU kan meerdere regels zijn)
        const maxLines = Math.max(productLines.length, skuLines.length);
        cursorY = Math.max(cursorY, startY + (maxLines * 4) + 2);
      });

      cursorY += 6;
      if (totalCbm > 0) {
        doc.setFontSize(11).text(`Total CBM: ${totalCbm.toFixed(3)}`, pageWidth - 14, cursorY, { align: 'right' });
        cursorY += 6;
      }
      const pdfTotal = (order.regels || []).reduce((s, r) => s + ((r.aantal || 0) * getRegelEffectivePrice(r)), 0);
      doc.setFontSize(12).text(`Total: ${fmtCurr(pdfTotal)}`, pageWidth - 14, cursorY, { align: 'right' });

      doc.save(`purchase-order-${order.ordernummer || order.id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Kon PDF niet genereren. Probeer het opnieuw.');
    }
  };

  const handleDownloadProposalPdf = async (leverancier) => {
    if (!leverancier) return;
    try {
      const jsPDFConstructor = await loadJsPdfLibrary();
      const doc = new jsPDFConstructor();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let totalCbm = 0;

      const logoDataUrl = await fetchLogoDataUrl();
      if (logoDataUrl) {
        try {
          const props = doc.getImageProperties(logoDataUrl);
          const targetWidth = 35;
          const targetHeight = (props.height / props.width) * targetWidth;
          doc.addImage(logoDataUrl, 'PNG', 14, 12, targetWidth, targetHeight);
        } catch (e) {
          doc.addImage(logoDataUrl, 'PNG', 14, 12, 35, 18);
        }
      }

      doc.setFontSize(16).text('Purchase Proposal', pageWidth - 14, 18, { align: 'right' });
      doc.setFontSize(10);
      doc.text(companyInfo.name || 'Company Name', pageWidth - 14, 26, { align: 'right' });
      if (companyInfo.address) doc.text(companyInfo.address, pageWidth - 14, 32, { align: 'right' });
      if (companyInfo.email) doc.text(companyInfo.email, pageWidth - 14, 38, { align: 'right' });
      if (companyInfo.phone) doc.text(companyInfo.phone, pageWidth - 14, 44, { align: 'right' });
      if (companyInfo.website) doc.text(companyInfo.website, pageWidth - 14, 50, { align: 'right' });

      let cursorY = 60;
      doc.setFontSize(12).text('Supplier', 14, cursorY, { underline: true });
      cursorY += 6;
      doc.setFontSize(10);
      doc.text(leverancier.leverancier_naam || 'Supplier', 14, cursorY);
      cursorY += 7;

      doc.setFontSize(11);
      doc.text('Product', 14, cursorY);
      doc.text('Quantity', 140, cursorY, { align: 'right' });
      doc.text('CBM', 170, cursorY, { align: 'right' });
      doc.text('Status', pageWidth - 14, cursorY, { align: 'right' });
      doc.line(14, cursorY + 2, pageWidth - 14, cursorY + 2);
      cursorY += 8;

      doc.setFontSize(10);
      const lineHeight = 5;
      (leverancier.producten || []).forEach((product) => {
        const aantal = product.aangepast_aantal !== null && product.aangepast_aantal !== undefined 
          ? product.aangepast_aantal 
          : product.voorgesteld_aantal || 0;
        const cbmPer = product.cbm_per_stuk || 0;
        const lineCbm = cbmPer ? (aantal * cbmPer) : 0;
        const cbm = cbmPer ? lineCbm.toFixed(3) : '-';
        totalCbm += lineCbm;
        const productName = product.product_naam || product.naam || 'Product';
        const nameLines = doc.splitTextToSize(productName, 110);
        const skuText = `SKU: ${product.product_sku || product.sku || '-'}`;
        const skuLines = doc.splitTextToSize(skuText, 110);
        const rowHeight = Math.max((nameLines.length + skuLines.length) * lineHeight, lineHeight);

        if (cursorY + rowHeight > pageHeight - 20) {
          doc.addPage();
          cursorY = 20;
        }

        nameLines.forEach((line, idx) => {
          doc.text(line, 14, cursorY + idx * lineHeight);
        });
        doc.setTextColor(120);
        skuLines.forEach((line, idx) => {
          doc.text(line, 14, cursorY + (nameLines.length + idx) * lineHeight);
        });
        doc.setTextColor(0);

        const valueY = cursorY + lineHeight;
        doc.text(String(aantal), 140, valueY, { align: 'right' });
        doc.text(String(cbm), 170, valueY, { align: 'right' });
        doc.text(product.status || 'concept', pageWidth - 14, valueY, { align: 'right' });

        cursorY += rowHeight + 2;
      });

      cursorY += 4;
      if (totalCbm > 0) {
        doc.setFontSize(10);
        doc.text('Total CBM:', 140, cursorY, { align: 'left' });
        doc.text(totalCbm.toFixed(3), 170, cursorY, { align: 'right' });
      }

      doc.save(`purchase-proposal-${leverancier.leverancier_naam || leverancier.leverancier_id}.pdf`);
    } catch (error) {
      console.error('Error generating proposal PDF:', error);
      alert('Kon voorstel-PDF niet genereren. Probeer het opnieuw.');
    }
  };

  const handleOrderStatusChange = async (id, status) => {
    try {
      await api.put(`/inkooporders/${id}/status`, { status });
      fetchInkooporders();
      if (showInkooporderDetailModal) {
        const response = await api.get(`/inkooporders/${id}`);
        setSelectedOrder(response.data);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Fout bij bijwerken status: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteOrder = async (id) => {
    // Bevestiging met ordernummer voor duidelijkheid
    const order = inkooporders.find(o => o.id === id);
    const ordernummer = order?.ordernummer || id;
    
    if (!window.confirm(`Weet je zeker dat je order "${ordernummer}" volledig wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`)) {
      return;
    }

    try {
      await api.delete(`/inkooporders/${id}`);
      fetchInkooporders();
      // Sluit detail modal als deze order wordt verwijderd
      if (showInkooporderDetailModal && selectedOrder?.id === id) {
        setShowInkooporderDetailModal(false);
        setSelectedOrder(null);
      }
    } catch (error) {
      console.error('Error deleting inkooporder:', error);
      alert('Fout bij verwijderen: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateOrdernummer = async (orderId, newValue) => {
    try {
      const order = inkooporders.find(o => o.id === orderId) || (selectedOrder?.id === orderId ? selectedOrder : null);
      if (!order) return;

      const trimmedValue = newValue.trim();
      if (!trimmedValue) {
        alert('Ordernummer mag niet leeg zijn');
        setEditingOrdernummer(null);
        return;
      }

      await api.put(`/inkooporders/${orderId}/ordernummer`, { ordernummer: trimmedValue });
      
      // Update local state
      setInkooporders(inkooporders.map(o => 
        o.id === orderId ? { ...o, ordernummer: trimmedValue } : o
      ));
      
      // Update selectedOrder if it's the same order
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, ordernummer: trimmedValue });
        setDetailForm((prev) => prev ? { ...prev, ordernummer: trimmedValue } : prev);
      }
      
      setEditingOrdernummer(null);
    } catch (error) {
      console.error('Error updating ordernummer:', error);
      alert('Fout bij bijwerken ordernummer: ' + (error.response?.data?.error || error.message));
      setEditingOrdernummer(null);
    }
  };

  const addInkooporderRegel = () => {
    setInkooporderFormData({
      ...inkooporderFormData,
      regels: [...inkooporderFormData.regels, { ean: '', product_id: '', aantal: 1, eenheidsprijs: 0 }]
    });
  };

  const updateInkooporderRegel = (index, field, value) => {
    const newRegels = [...inkooporderFormData.regels];
    
    if (field === 'ean') {
      // When EAN is entered, try to find product
      const normalizedEAN = normalizeEAN(value);
      
      // Try multiple matching strategies
      let suggestion = productSuggestions[normalizedEAN] || 
                       productSuggestions[value] ||
                       productSuggestions[value.trim()];
      
      // If still not found, try to find in producten array directly
      if (!suggestion && value) {
        const foundProduct = producten.find(p => {
          const pEAN = normalizeEAN(p.ean || '');
          const pSKU = normalizeEAN(p.sku || '');
          const inputEAN = normalizedEAN;
          return pEAN === inputEAN || pSKU === inputEAN || 
                 (p.ean && normalizeEAN(p.ean) === inputEAN) ||
                 (p.sku && normalizeEAN(p.sku) === inputEAN);
        });
        
        if (foundProduct) {
          suggestion = {
            naam: foundProduct.naam,
            prijs: foundProduct.inkoopprijs ?? 0,
            id: foundProduct.id,
            ean: foundProduct.ean,
            sku: foundProduct.sku
          };
          console.log(`[Inkooporders] Found product via direct search: ${foundProduct.naam}`);
        }
      }
      
      if (suggestion) {
        newRegels[index].product_id = suggestion.id;
        newRegels[index].eenheidsprijs = suggestion.inkoopprijs ?? 0;
        newRegels[index].product_naam = suggestion.naam;
        console.log(`[Inkooporders] EAN ${value} matched to product: ${suggestion.naam}`);
      } else {
        // Clear product if EAN not found
        newRegels[index].product_id = '';
        newRegels[index].product_naam = '';
        console.log(`[Inkooporders] EAN ${value} (normalized: ${normalizedEAN}) not found. Available keys:`, Object.keys(productSuggestions).slice(0, 5));
      }
      newRegels[index].ean = value;
    } else {
      newRegels[index][field] = field === 'aantal' || field === 'eenheidsprijs' ? parseFloat(value) || 0 : value;
      
      // Auto-fill price from product when selected from dropdown
      if (field === 'product_id') {
        const product = producten.find(p => p.id === parseInt(value));
        if (product) {
          newRegels[index].eenheidsprijs = product.inkoopprijs ?? 0;
          newRegels[index].ean = product.ean || product.sku || '';
        }
      }
    }
    
    setInkooporderFormData({ ...inkooporderFormData, regels: newRegels });
  };

  const removeInkooporderRegel = (index) => {
    setInkooporderFormData({
      ...inkooporderFormData,
      regels: inkooporderFormData.regels.filter((_, i) => i !== index)
    });
  };

  const resetInkooporderForm = () => {
    setInkooporderFormData({
      leverancier_id: '',
      verwachte_leverdatum: '',
      ata_tijd: '',
      opmerkingen: '',
      interne_notitie: '',
      regels: []
    });
    setEditingOrderId(null);
  };

  const getStatusBadge = (status) => {
    const statuses = {
      'concept': { label: 'Nieuw voorstel', class: 'status-concept' },
      'in_productie': { label: 'In Productie', class: 'status-in-productie' },
      'onderweg': { label: 'Onderweg', class: 'status-onderweg' },
      'geleverd': { label: 'Geleverd', class: 'status-geleverd' }
    };
    return statuses[status] || { label: status, class: '' };
  };

  const getUitverkoopStatusMeta = (status) => {
    switch (status) {
      case 'uitverkocht':
        return { color: '#999', label: 'Uitverkocht' };
      case 'kritiek':
        return { color: '#ff4444', label: 'Kritiek' };
      case 'laag':
        return { color: '#ff8800', label: 'Laag' };
      case 'waarschuwing':
        return { color: '#ffaa00', label: 'Waarschuwing' };
      case 'onder_minimum':
        return { color: '#ff4444', label: 'Onder Minimum' };
      case 'onvoldoende_data':
        return { color: '#999', label: 'Onvoldoende Data' };
      default:
        return { color: '#3FDFCF', label: 'Voldoende' };
    }
  };

  const updateProposalAmount = (leverancierId, product, newValue) => {
    const productKey = product.product_id || product.id;
    const updatedProposals = { ...proposals };
    updatedProposals.per_leverancier = (proposals?.per_leverancier || []).map((lev) => {
      if (lev.leverancier_id !== leverancierId) return lev;
      return {
        ...lev,
        producten: (lev.producten || []).map((p) => {
          if ((p.product_id || p.id) === productKey) {
            return { ...p, aangepast_aantal: newValue };
          }
          return p;
        })
      };
    });
    setProposals(updatedProposals);
  };

  const removeProposalFromCurrent = (leverancierId, product) => {
    const productKey = product.product_id || product.id;
    const label = product.product_naam || product.naam;
    if (!window.confirm(`Weet je zeker dat je "${label}" wilt verwijderen uit het voorstel?`)) {
      return;
    }

    const updatedProposals = { ...proposals };
    updatedProposals.per_leverancier = (proposals?.per_leverancier || []).map((lev) => {
      if (lev.leverancier_id !== leverancierId) return lev;
      return {
        ...lev,
        producten: (lev.producten || []).filter((p) => (p.product_id || p.id) !== productKey)
      };
    }).filter((lev) => lev.producten.length > 0);
    setProposals(updatedProposals);
  };

  const renderOrderActionButtons = (order, compact = false) => (
    <div className={`action-buttons ${compact ? 'mobile-action-buttons' : ''}`}>
      <button className="btn-icon" onClick={() => handleViewOrderDetail(order.id)} title="Bekijken">
        <FiEye />
      </button>
      <button className="btn-icon" onClick={() => handleDownloadOrderPdf(order)} title="Download PDF">
        <FiDownload />
      </button>
      {(order.status === 'open' || order.status === 'bevestigd' || order.status === 'in_productie') && (
        <button className="btn-icon" onClick={() => handleEditOrder(order.id)} title="Bewerken">
          <FiEdit2 />
        </button>
      )}
      {order.status === 'open' && (
        <>
          <button className="btn-icon btn-success" onClick={() => handleOrderStatusChange(order.id, 'bevestigd')} title="Bevestigen">
            <FiCheck />
          </button>
          <button className="btn-icon" style={{ color: '#ff8800' }} onClick={() => handleOrderStatusChange(order.id, 'in_productie')} title="In Productie">
            <FiTruck />
          </button>
        </>
      )}
      {order.status === 'bevestigd' && (
        <>
          <button className="btn-icon" style={{ color: '#ff8800' }} onClick={() => handleOrderStatusChange(order.id, 'in_productie')} title="In Productie">
            <FiTruck />
          </button>
          <button className="btn-icon btn-success" onClick={() => handleOrderStatusChange(order.id, 'geleverd')} title="Markeer als Geleverd">
            <FiCheck />
          </button>
        </>
      )}
      {order.status === 'in_productie' && (
        <>
          <button className="btn-icon btn-primary" onClick={() => handleOrderStatusChange(order.id, 'bevestigd')} title="Terug naar Bevestigd">
            <FiCheck />
          </button>
          <button className="btn-icon btn-success" onClick={() => handleOrderStatusChange(order.id, 'geleverd')} title="Markeer als Geleverd">
            <FiCheck />
          </button>
        </>
      )}
      <button
        className="btn-icon btn-danger"
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteOrder(order.id);
        }}
        title="Verwijderen"
      >
        <FiTrash2 />
      </button>
    </div>
  );

  const calculateInkooporderTotal = () => {
    return inkooporderFormData.regels.reduce((sum, regel) => {
      return sum + (regel.aantal * regel.eenheidsprijs);
    }, 0);
  };

  // Filter en sorteer inkooporders: zoekterm + volgorde Nieuw voorstel → In productie → Onderweg → Geleverd
  const filteredInkooporders = useMemo(() => {
    let list = inkooporders;
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      list = inkooporders.filter(order => {
        const ordernummer = String(order.ordernummer || '').toLowerCase();
        const leverancierNaam = String(order.leverancier_naam || '').toLowerCase();
        const opmerkingen = String(order.opmerkingen || '').toLowerCase();
        const interneNotitie = String(order.interne_notitie || '').toLowerCase();
        return ordernummer.includes(searchLower) ||
               leverancierNaam.includes(searchLower) ||
               opmerkingen.includes(searchLower) ||
               interneNotitie.includes(searchLower);
      });
    }
    const statusRank = { concept: 1, in_productie: 2, onderweg: 3, geleverd: 4 };
    return [...list].sort((a, b) => {
      const ra = statusRank[a.status] || 99;
      const rb = statusRank[b.status] || 99;
      if (ra !== rb) return ra - rb;
      const da = a.datum ? new Date(a.datum).getTime() : 0;
      const db = b.datum ? new Date(b.datum).getTime() : 0;
      return db - da;
    });
  }, [inkooporders, searchTerm]);

  const productById = useMemo(() => {
    const map = {};
    (producten || []).forEach((p) => {
      if (p?.id) map[p.id] = p;
    });
    return map;
  }, [producten]);

  const productBySku = useMemo(() => {
    const map = {};
    (producten || []).forEach((p) => {
      if (p?.sku) map[p.sku] = p;
    });
    return map;
  }, [producten]);

  const getProductCbm = useCallback((product) => {
    const direct = product?.cbm_per_stuk ?? product?.cbm;
    if (direct !== null && direct !== undefined && !Number.isNaN(Number(direct))) {
      return Number(direct);
    }
    const fromId = productById[product?.product_id] || productById[product?.id];
    if (fromId && fromId.cbm_per_stuk) return Number(fromId.cbm_per_stuk) || 0;
    const fromSku = product?.product_sku && productBySku[product.product_sku];
    if (fromSku && fromSku.cbm_per_stuk) return Number(fromSku.cbm_per_stuk) || 0;
    return 0;
  }, [productById, productBySku]);

  const grandTotalCBMProposals = useMemo(() => {
    if (!proposals?.per_leverancier?.length) return 0;
    let total = 0;
    proposals.per_leverancier.forEach(lev => {
      (lev.producten || []).forEach(p => {
        const a = p.aangepast_aantal ?? p.voorgesteld_aantal ?? 0;
        total += a * getProductCbm(p);
      });
    });
    return total;
  }, [proposals, getProductCbm]);

  /** CBM per leverancier uit huidig voorstel (voor signaal op Nieuw voorstel-tab) */
  const cbmPerLeverancier = useMemo(() => {
    const map = {};
    if (!proposals?.per_leverancier?.length) return map;
    proposals.per_leverancier.forEach(lev => {
      let cbm = 0;
      (lev.producten || []).forEach(p => {
        const a = p.aangepast_aantal ?? p.voorgesteld_aantal ?? 0;
        cbm += a * getProductCbm(p);
      });
      map[lev.leverancier_id] = Math.round(cbm * 1000) / 1000;
    });
    return map;
  }, [proposals, getProductCbm]);

  const grandTotalCBMSaved = useMemo(() => {
    if (!savedProposals?.per_leverancier?.length) return 0;
    let total = 0;
    savedProposals.per_leverancier.forEach(lev => {
      (lev.producten || []).forEach(p => {
        const a = p.aangepast_aantal ?? p.voorgesteld_aantal ?? 0;
        total += a * getProductCbm(p);
      });
    });
    return total;
  }, [savedProposals, getProductCbm]);

  // Detail edit helpers
  const addDetailRegel = () => {
    if (!detailForm) return;
    setDetailForm({
      ...detailForm,
      regels: [...detailForm.regels, { product_id: '', aantal: 1, eenheidsprijs: 0, ean: '' }]
    });
  };

  const updateDetailRegel = (index, field, value) => {
    if (!detailForm) return;
    const regels = [...detailForm.regels];
    regels[index][field] = field === 'aantal' || field === 'eenheidsprijs' ? parseFloat(value) || 0 : value;

    if (field === 'product_id') {
      const product = producten.find(p => p.id === parseInt(value, 10));
      if (product) {
        regels[index].eenheidsprijs = product.inkoopprijs ?? 0;
        if (product.sku) regels[index].ean = product.sku;
        if (product.naam) regels[index].product_naam = product.naam;
        if (product.cbm_per_stuk) regels[index].cbm_per_stuk = product.cbm_per_stuk;
      }
    }

    if (field === 'ean') {
      const normalizedEAN = normalizeEAN(value);
      const suggestion = productSuggestions[normalizedEAN] || productSuggestions[value] || productSuggestions[value.trim()];
      if (suggestion) {
        regels[index].product_id = suggestion.id;
        regels[index].product_naam = suggestion.naam;
        regels[index].eenheidsprijs = suggestion.inkoopprijs ?? 0;
        if (suggestion.cbm_per_stuk) regels[index].cbm_per_stuk = suggestion.cbm_per_stuk;
      }
    }

    setDetailForm({ ...detailForm, regels });
  };

  const removeDetailRegel = (index) => {
    if (!detailForm) return;
    setDetailForm({
      ...detailForm,
      regels: detailForm.regels.filter((_, i) => i !== index)
    });
  };

  const saveDetailChanges = async () => {
    if (!selectedOrder || !detailForm) return;

    if (!detailForm.leverancier_id) {
      alert('Selecteer een leverancier');
      return;
    }

    if (!detailForm.regels || detailForm.regels.length === 0) {
      alert('Voeg minimaal één product toe');
      return;
    }

    try {
      const newOrdernummer = String(detailForm.ordernummer || '').trim();
      const currentOrdernummer = String(selectedOrder.ordernummer || '').trim();
      if (!newOrdernummer) {
        alert('Ordernummer mag niet leeg zijn');
        return;
      }
      if (newOrdernummer !== currentOrdernummer) {
        await handleUpdateOrdernummer(selectedOrder.id, newOrdernummer);
      }

      await api.put(`/inkooporders/${selectedOrder.id}`, {
        ...detailForm,
        verwachte_leverdatum: detailForm.verwachte_leverdatum || null,
        ata_tijd: detailForm.ata_tijd || null
      });
      await fetchInkooporders();
      await handleViewOrderDetail(selectedOrder.id);
      setDetailEditMode(false);
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Fout bij opslaan: ' + (error.response?.data?.error || error.message));
    }
  };

  const renderProposals = (proposalsData, isSaved = false) => {
    if (!proposalsData || !proposalsData.per_leverancier || proposalsData.per_leverancier.length === 0) {
      if (!isSaved && proposalsData && proposalsData.totaal_producten === 0) {
        const leverancierNaam = proposalsData.leverancier_naam ||
          (selectedLeverancier ? leveranciers.find(l => String(l.id) === String(selectedLeverancier))?.naam : null);
        return (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '10px',
            padding: '1.5rem 2rem', textAlign: 'center', color: '#155724', marginTop: '1rem'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
            <strong style={{ fontSize: '1.1rem' }}>
              Alles op voorraad{leverancierNaam ? ` bij ${leverancierNaam}` : ''}!
            </strong>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
              Er zijn momenteel geen producten die besteld moeten worden. De voorraad dekt de verwachte verkoop voor de ingestelde periode.
            </p>
          </div>
        );
      }
      return (
        <div className="empty-state">
          {isSaved ? 'Geen opgeslagen voorstellen' : 'Bereken eerst een voorstel'}
        </div>
      );
    }

    // Filter op zoekterm als die er is
    let filteredLeveranciers = proposalsData.per_leverancier;
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filteredLeveranciers = proposalsData.per_leverancier.map(leverancier => {
        const filteredProducten = leverancier.producten.filter(product => {
          const productNaam = String(product.product_naam || product.naam || '').toLowerCase();
          const productSku = String(product.product_sku || product.sku || '').toLowerCase();
          const leverancierNaam = String(leverancier.leverancier_naam || '').toLowerCase();
          
          return productNaam.includes(searchLower) || 
                 productSku.includes(searchLower) ||
                 leverancierNaam.includes(searchLower);
        });
        
        return {
          ...leverancier,
          producten: filteredProducten
        };
      }).filter(leverancier => leverancier.producten.length > 0);
    }

    if (filteredLeveranciers.length === 0) {
      return (
        <div className="empty-state">
          Geen voorstellen gevonden voor "{searchTerm}"
        </div>
      );
    }

    return filteredLeveranciers.map((leverancier) => {
      const levId = leverancier.leverancier_id;
      const accorded = accordedAdvies[levId] || {};
      // Bereken totaal CBM voor deze leverancier (inclusief geaccordeerde adviesregels)
      let totaalCBM = leverancier.producten.reduce((sum, product) => {
        const aantal = product.aangepast_aantal !== null && product.aangepast_aantal !== undefined 
          ? product.aangepast_aantal 
          : product.voorgesteld_aantal || 0;
        const cbm = getProductCbm(product);
        return sum + (aantal * cbm);
      }, 0);
      (leverancier.advies_producten || []).forEach(adv => {
        const aantal = accorded[adv.product_id] ?? 0;
        if (aantal > 0) totaalCBM += aantal * (getProductCbm(adv) || 0);
      });
      
      // Opgeslagen voorstellen standaard open zodat verwijderknop zichtbaar is
      const isCollapsed = collapsedSuppliers[`${isSaved ? 'saved' : 'new'}-${leverancier.leverancier_id}`] ?? !isSaved;
      const manualForm = manualAddForms[leverancier.leverancier_id] || { productId: '', aantal: 1 };
      const supplierProducts = producten.filter(p => Number(p.leverancier_id) === Number(leverancier.leverancier_id));

      return (
      <div key={leverancier.leverancier_id} className="leverancier-group">
        <div className="leverancier-header">
          <h3>{leverancier.leverancier_naam}</h3>
          <div className="leverancier-info">
            <span>Levertijd: {leverancier.levertijd_dagen || 7} dagen</span>
            <span>{leverancier.producten.length} product(en)</span>
            {totaalCBM > 0 && (
              <span><strong>Totaal CBM: {totaalCBM.toFixed(3)}</strong></span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => handleDownloadProposalPdf(leverancier)}
              title="Download voorstel als PDF"
            >
              <FiDownload /> PDF
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                if (isSaved) {
                  handleDeleteLeverancierProposals(leverancier);
                } else {
                  if (!window.confirm(`Voorstel "${leverancier.leverancier_naam}" verwijderen uit overzicht?`)) return;
                  const updated = { ...proposals };
                  updated.per_leverancier = proposals.per_leverancier.filter(
                    l => l.leverancier_id !== leverancier.leverancier_id
                  );
                  setProposals(updated.per_leverancier.length ? updated : null);
                }
              }}
              title="Verwijderen"
              style={{ color: '#c62828', borderColor: '#c62828' }}
            >
              <FiTrash2 /> Verwijderen
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setCollapsedSuppliers(prev => ({
                  ...prev,
                  [`${isSaved ? 'saved' : 'new'}-${leverancier.leverancier_id}`]: !isCollapsed
                }));
              }}
            >
              {isCollapsed ? 'Openen' : 'Sluiten'}
            </button>
          </div>
        </div>

        {!isCollapsed && (() => {
          // Advies bij minimum CBM/stuks (we verhogen aantallen niet automatisch)
          const minimumAdvies = leverancier.minimum_advies;
          // Kolom-definities — labels, uitlijning, drag-index
          const baseCols = [
            { label: 'Product', title: 'Productnaam', align: 'left', idx: 0 },
            { label: 'SKU', title: 'SKU / Artikelnummer', align: 'left', idx: 1 },
            { label: 'Voorraad', title: 'Huidige voorraad', align: 'right', idx: 2 },
            { label: 'Onderweg', title: 'Onderweg', align: 'right', idx: 3 },
            { label: 'Prod.', title: 'In productie', align: 'right', idx: 4 },
            { label: 'Totaal', title: 'Totaal voorraad', align: 'right', idx: 5 },
            { label: 'Min.', title: 'Minimum voorraad', align: 'right', idx: 6 },
            { label: 'Verk./mnd', title: 'Verwachte verkoop per maand', align: 'right', idx: 7 },
            { label: 'Seizoen', title: 'Seizoensfactor', align: 'right', idx: 8 },
            { label: 'Voorstel', title: 'Voorgesteld aantal', align: 'right', idx: 9 },
            { label: 'CBM/st', title: 'CBM per stuk', align: 'right', idx: 10 },
            { label: 'CBM tot', title: 'Totaal CBM', align: 'right', idx: 11 },
          ];
          const savedCols = isSaved ? [
            { label: 'Aangep.', title: 'Aangepast aantal', align: 'right', idx: 12 },
            { label: 'Status', title: 'Status', align: 'left', idx: 13 },
          ] : [];
          const actionCol = { label: 'Acties', title: 'Acties', align: 'left', idx: isSaved ? 14 : 12 };
          const allCols = [...baseCols, ...savedCols, actionCol];

          const startResize = (e, colIdx) => {
            e.preventDefault();
            const startX = e.clientX;
            const startW = colWidths[colIdx] || 60;
            resizingCol.current = colIdx;
            const onMove = (me) => {
              const newW = Math.max(30, startW + me.clientX - startX);
              setColWidths(prev => { const n = [...prev]; n[colIdx] = newW; return n; });
            };
            const onUp = () => {
              resizingCol.current = null;
              window.removeEventListener('mousemove', onMove);
              window.removeEventListener('mouseup', onUp);
            };
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
          };

          return (
        <>
          {minimumAdvies && minimumAdvies.length > 0 && (
            <div className="minimum-order-advies" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9', fontSize: '0.9rem' }}>
              <strong>Advies minimumorder:</strong>
              <ul style={{ margin: '0.35rem 0 0 1rem', paddingLeft: '0.5rem' }}>
                {minimumAdvies.map((tekst, i) => (
                  <li key={i}>{tekst}</li>
                ))}
              </ul>
            </div>
          )}
        <div className="proposal-mobile-list">
          {leverancier.producten.map((product) => {
            const totaalVoorraad = (product.huidige_voorraad || 0) + (product.voorraad_onderweg || 0) + (product.in_productie || 0);
            const aantal = product.aangepast_aantal ?? product.voorgesteld_aantal ?? 0;
            const productKey = product.id || `${product.product_id}-${product.leverancier_id}`;
            const cbmPerStuk = getProductCbm(product);
            const totaalCbmProduct = aantal * cbmPerStuk;

            return (
              <div key={productKey} className="mobile-data-card">
                <div className="mobile-data-card-top">
                  <div>
                    <h4><ProductNaamMetKlasse productId={product.product_id ?? product.id} naam={product.product_naam || product.naam} /></h4>
                    <p>{product.product_sku || product.sku || '-'}</p>
                  </div>
                  {isSaved ? (
                    <input
                      type="checkbox"
                      checked={selectedProposals.has(product.id)}
                      onChange={() => toggleProposalSelection(product.id)}
                    />
                  ) : null}
                </div>
                <div className="mobile-data-grid">
                  <div><span>Voorraad</span><strong>{product.huidige_voorraad || 0}</strong></div>
                  <div><span>Onderweg</span><strong>{product.voorraad_onderweg || 0}</strong></div>
                  <div><span>In productie</span><strong>{product.in_productie || 0}</strong></div>
                  <div><span>Totaal</span><strong>{totaalVoorraad}</strong></div>
                  <div><span>Minimum</span><strong>{product.minimum_voorraad || 0}</strong></div>
                  <div><span>Verkoop/mnd</span><strong>{product.verwachte_verkoop || '-'}</strong></div>
                  <div><span>Seizoen</span><strong>{product.seizoensfactor ? product.seizoensfactor.toFixed(2) : '1.00'}</strong></div>
                  <div><span>CBM/st</span><strong>{cbmPerStuk > 0 ? cbmPerStuk.toFixed(3) : '-'}</strong></div>
                  <div><span>CBM totaal</span><strong>{totaalCbmProduct > 0 ? totaalCbmProduct.toFixed(3) : '-'}</strong></div>
                  {isSaved ? (
                    <div><span>Status</span><strong>{product.status === 'concept' ? 'Concept' : product.status === 'definitief' ? 'Definitief' : product.status === 'geconverteerd' ? 'Geconverteerd' : '-'}</strong></div>
                  ) : null}
                </div>
                {product.berekening_uitleg ? (
                  <div className="mobile-card-note">
                    Basis: {product.verwachte_verkoop_basis || '-'} / maand
                    {product.voorgesteld_aantal > 0 && !isSaved ? ` · ${product.berekening_uitleg.voorgesteld}` : ''}
                  </div>
                ) : null}
                <div className="mobile-data-card-actions">
                  {!isSaved ? (
                    <label className="mobile-inline-field">
                      <span>Voorstel</span>
                      <input
                        type="number"
                        min="0"
                        value={aantal}
                        onChange={(e) => updateProposalAmount(leverancier.leverancier_id, product, parseInt(e.target.value, 10) || 0)}
                      />
                    </label>
                  ) : (
                    <div className="mobile-pill-value">
                      Aangepast: <strong>{product.aangepast_aantal !== null ? product.aangepast_aantal : product.voorgesteld_aantal || 0}</strong>
                    </div>
                  )}
                  <div className="mobile-icon-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleShowCalculation(product.product_id || product.id)}
                      title="Bekijk berekening"
                    >
                      <FiInfo />
                    </button>
                    {!isSaved ? (
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => removeProposalFromCurrent(leverancier.leverancier_id, product)}
                        title="Verwijderen uit voorstel"
                      >
                        <FiX />
                      </button>
                    ) : (
                      <>
                        <button className="btn-icon" onClick={() => handleEdit(product)} title="Bewerken">
                          <FiEdit2 />
                        </button>
                        <button className="btn-icon btn-danger" onClick={() => handleDeleteProposal(product.id)} title="Voorstel verwijderen">
                          <FiTrash2 />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {!isSaved && (leverancier.advies_producten || []).map((adv) => {
            const isAccorded = (accorded[adv.product_id] ?? 0) > 0;
            const accordAantal = accorded[adv.product_id] ?? adv.aanbevolen_aantal ?? adv.min_inkoop_aantal ?? 0;
            const totaalVoorraadAdv = (adv.huidige_voorraad || 0) + (adv.voorraad_onderweg || 0) + (adv.in_productie || 0);
            const adviesCbm = getProductCbm(adv) || 0;

            return (
              <div
                key={`advies-mobile-${adv.product_id}`}
                className={`mobile-data-card ${!isAccorded ? 'mobile-data-card-warning' : ''}`}
              >
                <div className="mobile-data-card-top">
                  <div>
                    <h4><ProductNaamMetKlasse productId={adv.product_id} naam={adv.product_naam} /></h4>
                    <p>{adv.product_sku || '-'} · Advies container vol</p>
                  </div>
                  <button type="button" className="btn-icon" onClick={() => handleShowCalculation(adv.product_id)} title="Bekijk berekening">
                    <FiInfo />
                  </button>
                </div>
                <div className="mobile-data-grid">
                  <div><span>Voorraad</span><strong>{adv.huidige_voorraad || 0}</strong></div>
                  <div><span>Totaal</span><strong>{totaalVoorraadAdv}</strong></div>
                  <div><span>Minimum</span><strong>{adv.minimum_voorraad || 0}</strong></div>
                  <div><span>Verkoop/mnd</span><strong>{adv.verwachte_verkoop != null ? Number(adv.verwachte_verkoop).toFixed(0) : '-'}</strong></div>
                  <div><span>Seizoen</span><strong>{adv.seizoensfactor != null ? Number(adv.seizoensfactor).toFixed(2) : '1.00'}</strong></div>
                  <div><span>CBM/st</span><strong>{adviesCbm > 0 ? adviesCbm.toFixed(3) : '-'}</strong></div>
                </div>
                {!isAccorded ? (
                  <div className="mobile-card-note">
                    Aanbevolen: <strong>{adv.aanbevolen_aantal}</strong> · Minimum: {adv.min_inkoop_aantal}
                  </div>
                ) : null}
                <div className="mobile-data-card-actions">
                  {!isAccorded ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => setAccordedAdvies(prev => ({
                        ...prev,
                        [levId]: { ...(prev[levId] || {}), [adv.product_id]: adv.aanbevolen_aantal ?? adv.min_inkoop_aantal }
                      }))}
                    >
                      <FiCheck /> Accordeer
                    </button>
                  ) : (
                    <>
                      <label className="mobile-inline-field">
                        <span>Aantal</span>
                        <input
                          type="number"
                          min={adv.min_inkoop_aantal || 1}
                          value={accordAantal}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10) || 0;
                            setAccordedAdvies(prev => ({
                              ...prev,
                              [levId]: { ...(prev[levId] || {}), [adv.product_id]: Math.max(adv.min_inkoop_aantal || 0, v) }
                            }));
                          }}
                        />
                      </label>
                      <div className="mobile-pill-value">
                        CBM totaal: <strong>{accordAantal > 0 ? (accordAantal * adviesCbm).toFixed(3) : '-'}</strong>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setAccordedAdvies(prev => {
                          const next = { ...prev };
                          if (next[levId]) {
                            const lev = { ...next[levId] };
                            delete lev[adv.product_id];
                            next[levId] = Object.keys(lev).length ? lev : undefined;
                          }
                          return next;
                        })}
                      >
                        Intrekken
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          <div className="mobile-data-card mobile-add-product-card">
            <h4>Product toevoegen</h4>
            <div className="mobile-inline-field mobile-inline-field-block">
              <span>Product</span>
              <select
                value={manualForm.productId}
                onChange={(e) => handleManualAddChange(leverancier.leverancier_id, 'productId', e.target.value)}
              >
                <option value="">Selecteer product om toe te voegen</option>
                {(supplierProducts.length > 0 ? supplierProducts : producten).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.naam} {p.sku ? `(${p.sku})` : ''} {p.ean ? `- ${p.ean}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="mobile-inline-field mobile-inline-field-block">
              <span>Aantal</span>
              <input
                type="number"
                min="1"
                value={manualForm.aantal}
                onChange={(e) => handleManualAddChange(leverancier.leverancier_id, 'aantal', e.target.value)}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={() => handleAddProductToProposal(leverancier.leverancier_id, isSaved)}
            >
              Product toevoegen
            </button>
            <div className="mobile-pill-value">
              Totaal CBM: <strong>{totaalCBM > 0 ? totaalCBM.toFixed(3) : '-'}</strong>
            </div>
          </div>
        </div>

        <div className="proposal-desktop-table" style={{ width: '100%', overflowX: 'auto' }}>
        <table className="proposal-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            {isSaved && <col style={{ width: '30px' }} />}
            {allCols.map(c => (
              <col key={c.idx} style={{ width: colWidths[c.idx] ? `${colWidths[c.idx]}px` : undefined }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {isSaved && (
                <th style={{ width: '30px', padding: '0.2rem' }}>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const ids = leverancier.producten.map(p => p.id || p.product_id).filter(Boolean);
                      const newSet = new Set(selectedProposals);
                      if (checked) ids.forEach(id => newSet.add(id));
                      else ids.forEach(id => newSet.delete(id));
                      setSelectedProposals(newSet);
                    }}
                    checked={leverancier.producten.every(p => selectedProposals.has(p.id || p.product_id))}
                  />
                </th>
              )}
              {allCols.map(c => (
                <th key={c.idx} title={c.title} style={{ textAlign: c.align, position: 'relative', userSelect: 'none' }}>
                  <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label}</span>
                  <span
                    onMouseDown={(e) => startResize(e, c.idx)}
                    style={{
                      position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px',
                      cursor: 'col-resize', background: 'transparent', zIndex: 1,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#3FDFCF'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leverancier.producten.map((product) => {
              const totaalVoorraad = (product.huidige_voorraad || 0) + (product.voorraad_onderweg || 0) + (product.in_productie || 0);

              return (
                <tr key={product.id || `${product.product_id}-${product.leverancier_id}`}>
                  {isSaved && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedProposals.has(product.id)}
                        onChange={() => toggleProposalSelection(product.id)}
                      />
                    </td>
                  )}
                  <td><ProductNaamMetKlasse productId={product.product_id ?? product.id} naam={product.product_naam || product.naam} strong /></td>
                  <td>{product.product_sku || product.sku || '-'}</td>
                  <td>{product.huidige_voorraad || 0}</td>
                  <td>{product.voorraad_onderweg || 0}</td>
                  <td>{product.in_productie || 0}</td>
                  <td><strong>{totaalVoorraad}</strong></td>
                  <td>{product.minimum_voorraad || 0}</td>
                  <td>
                    {product.verwachte_verkoop || '-'}
                    {product.berekening_uitleg && (
                      <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                        <div>Basis: {product.verwachte_verkoop_basis || '-'}/maand</div>
                        <div>Jaar: {product.historische_verkoop_jaar || 0} stuks</div>
                      </div>
                    )}
                  </td>
                  <td>
                    {product.seizoensfactor ? product.seizoensfactor.toFixed(2) : '1.00'}
                    {product.seizoensfactor && product.seizoensfactor > 1 && (
                      <span style={{ fontSize: '0.75rem', color: '#ff8800', display: 'block' }}>↑ Seizoen</span>
                    )}
                    {product.seizoensfactor && product.seizoensfactor < 1 && (
                      <span style={{ fontSize: '0.75rem', color: '#999', display: 'block' }}>↓ Laag seizoen</span>
                    )}
                  </td>
                  <td>
                    {isSaved ? (
                      <strong>{product.aangepast_aantal !== null ? product.aangepast_aantal : product.voorgesteld_aantal || 0}</strong>
                    ) : (
                      <input
                        type="number"
                        min="0"
                        value={product.aangepast_aantal !== undefined ? product.aangepast_aantal : product.voorgesteld_aantal || 0}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value) || 0;
                          updateProposalAmount(leverancier.leverancier_id, product, newValue);
                        }}
                        style={{ width: '60px', padding: '0.25rem', textAlign: 'center' }}
                      />
                    )}
                    {product.berekening_uitleg && product.voorgesteld_aantal > 0 && !isSaved && (
                      <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.25rem', fontStyle: 'italic' }}>
                        {product.berekening_uitleg.voorgesteld}
                      </div>
                    )}
                  </td>
                  <td>
                    {(() => {
                      const cbm = getProductCbm(product);
                      return cbm > 0 ? cbm.toFixed(3) : '-';
                    })()}
                  </td>
                  <td>
                    {(() => {
                      const aantal = product.aangepast_aantal !== null && product.aangepast_aantal !== undefined 
                        ? product.aangepast_aantal 
                        : product.voorgesteld_aantal || 0;
                      const cbm = getProductCbm(product);
                      const totaalCBM = aantal * cbm;
                      return totaalCBM > 0 ? totaalCBM.toFixed(3) : '-';
                    })()}
                  </td>
                  {isSaved && (
                    <td>
                      {product.aangepast_aantal !== null ? (
                        <strong style={{ color: '#3FDFCF' }}>{product.aangepast_aantal}</strong>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </td>
                  )}
                  {isSaved && (
                    <td>
                      <span className={`status-badge status-${product.status}`}>
                        {product.status === 'concept' && 'Concept'}
                        {product.status === 'definitief' && 'Definitief'}
                        {product.status === 'geconverteerd' && 'Geconverteerd'}
                      </span>
                    </td>
                  )}
                  <td>
                    <button
                      className="btn-icon"
                      onClick={() => handleShowCalculation(product.product_id || product.id)}
                      title="Bekijk berekening"
                      style={{ marginRight: '0.5rem' }}
                    >
                      <FiInfo />
                    </button>
                    {!isSaved && (
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => removeProposalFromCurrent(leverancier.leverancier_id, product)}
                        title="Verwijderen uit voorstel"
                      >
                        <FiX />
                      </button>
                    )}
                    {isSaved && (
                      <>
                        <button
                          className="btn-icon"
                          onClick={() => handleEdit(product)}
                          title="Bewerken"
                          style={{ marginRight: '0.25rem' }}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDeleteProposal(product.id)}
                          title="Voorstel verwijderen"
                        >
                          <FiTrash2 />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {/* Adviesrijen (container vol): alleen als min CBM niet bereikt, max 10 */}
            {!isSaved && (leverancier.advies_producten || []).map((adv) => {
              const isAccorded = (accorded[adv.product_id] ?? 0) > 0;
              const accordAantal = accorded[adv.product_id] ?? adv.aanbevolen_aantal ?? adv.min_inkoop_aantal ?? 0;
              const totaalVoorraadAdv = (adv.huidige_voorraad || 0) + (adv.voorraad_onderweg || 0) + (adv.in_productie || 0);
              return (
                <tr
                  key={`advies-${adv.product_id}`}
                  style={!isAccorded ? { backgroundColor: 'rgba(255, 193, 7, 0.12)' } : undefined}
                >
                  {isSaved && <td></td>}
                  <td>
                    <ProductNaamMetKlasse productId={adv.product_id} naam={adv.product_naam} strong />
                    {!isAccorded && <span style={{ fontSize: '0.75rem', color: '#888', display: 'block' }}>Advies (container vol)</span>}
                  </td>
                  <td>{adv.product_sku || '-'}</td>
                  <td>{adv.huidige_voorraad || 0}</td>
                  <td>{adv.voorraad_onderweg || 0}</td>
                  <td>{adv.in_productie || 0}</td>
                  <td><strong>{totaalVoorraadAdv}</strong></td>
                  <td>{adv.minimum_voorraad || 0}</td>
                  <td>{adv.verwachte_verkoop != null ? Number(adv.verwachte_verkoop).toFixed(0) : '-'}</td>
                  <td>{adv.seizoensfactor != null ? Number(adv.seizoensfactor).toFixed(2) : '1.00'}</td>
                  <td>
                    {!isAccorded ? (
                      <>
                        <div style={{ fontSize: '0.85rem' }}>Aanbev.: <strong>{adv.aanbevolen_aantal}</strong>, Min.: {adv.min_inkoop_aantal}</div>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: '0.35rem' }}
                          onClick={() => setAccordedAdvies(prev => ({
                            ...prev,
                            [levId]: { ...(prev[levId] || {}), [adv.product_id]: adv.aanbevolen_aantal ?? adv.min_inkoop_aantal }
                          }))}
                        >
                          <FiCheck /> Accordeer
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          min={adv.min_inkoop_aantal || 1}
                          value={accordAantal}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10) || 0;
                            setAccordedAdvies(prev => ({
                              ...prev,
                              [levId]: { ...(prev[levId] || {}), [adv.product_id]: Math.max(adv.min_inkoop_aantal || 0, v) }
                            }));
                          }}
                          style={{ width: '56px', padding: '0.25rem', textAlign: 'center' }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ marginLeft: '0.25rem', fontSize: '0.75rem' }}
                          onClick={() => setAccordedAdvies(prev => {
                            const next = { ...prev };
                            if (next[levId]) {
                              const lev = { ...next[levId] };
                              delete lev[adv.product_id];
                              next[levId] = Object.keys(lev).length ? lev : undefined;
                            }
                            return next;
                          })}
                          title="Intrekken"
                        >
                          Intrekken
                        </button>
                      </>
                    )}
                  </td>
                  <td>{adv.cbm_per_stuk != null ? Number(adv.cbm_per_stuk).toFixed(3) : '-'}</td>
                  <td>
                    {isAccorded && accordAantal > 0
                      ? ((accordAantal * (getProductCbm(adv) || 0)).toFixed(3))
                      : '-'}
                  </td>
                  {isSaved && <td></td>}
                  {isSaved && <td></td>}
                  <td>
                    <button type="button" className="btn-icon" onClick={() => handleShowCalculation(adv.product_id)} title="Bekijk berekening">
                      <FiInfo />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            {true && (
              <tr>
                {isSaved && <td></td>}
                <td colSpan={isSaved ? 4 : 5}>
                  <select
                    value={manualForm.productId}
                    onChange={(e) => handleManualAddChange(leverancier.leverancier_id, 'productId', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem' }}
                  >
                    <option value="">Selecteer product om toe te voegen</option>
                    {(supplierProducts.length > 0 ? supplierProducts : producten).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.naam} {p.sku ? `(${p.sku})` : ''} {p.ean ? `- ${p.ean}` : ''}
                      </option>
                    ))}
                  </select>
                </td>
                <td colSpan="2">
                  <input
                    type="number"
                    min="1"
                    value={manualForm.aantal}
                    onChange={(e) => handleManualAddChange(leverancier.leverancier_id, 'aantal', e.target.value)}
                    style={{ width: '100%', padding: '0.5rem' }}
                  />
                </td>
                <td colSpan={isSaved ? 3 : 3}>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleAddProductToProposal(leverancier.leverancier_id, isSaved)}
                  >
                    Product toevoegen
                  </button>
                </td>
                <td colSpan={isSaved ? 3 : 2}></td>
              </tr>
            )}
            <tr style={{ background: '#f5f5f5', fontWeight: 'bold' }}>
              <td colSpan={isSaved ? 11 : 10}>Totaal CBM</td>
              <td></td>
              <td>{totaalCBM > 0 ? totaalCBM.toFixed(3) : '-'}</td>
              <td colSpan={isSaved ? 3 : 1}></td>
            </tr>
          </tfoot>
        </table>
        </div>
        </>
      );
    })()}
      </div>
      );
    });
  };

  const definitiefCount = savedProposals?.per_leverancier
    ?.flatMap(l => l.producten)
    .filter(p => p.status === 'definitief').length || 0;

  const rankingLimit = rankingFilters.limit || 10;
  const showTopRankingList = rankingFilters.lijst !== 'uitfaseren';
  const showPhaseOutRankingList = rankingFilters.lijst !== 'top';
  const rankingCategoryOptions = productRankingData?.beschikbare_categorien || [];
  const rankingContext = productRankingData?.ranking_context;
  const rankingSupplierScope = rankingContext?.scope === 'leverancier';
  const percentileLabel = rankingSupplierScope ? 'Lev. % (vraag/marge)' : '% (vraag/marge)';

  const topListHeading = rankingSupplierScope && rankingContext?.leverancier_naam
    ? `Beste ${rankingLimit} binnen ${rankingContext.leverancier_naam}`
    : `Top ${rankingLimit} topproducten`;

  const phaseOutHeading = rankingSupplierScope && rankingContext?.leverancier_naam
    ? `Uit te faseren binnen ${rankingContext.leverancier_naam}`
    : `Top ${rankingLimit} uit te faseren`;

  const rankingCompareSelectedIds = useMemo(
    () => new Set(rankingCompareItems.map((p) => p.id)),
    [rankingCompareItems]
  );

  const rankingCompareRows = useMemo(
    () => [
      { label: 'Totaalscore', get: (p) => p.score, higher: true, fmt: (v) => (v != null ? String(v) : '—') },
      { label: 'Rang (assortiment)', get: (p) => p.rang_totaal, higher: false, fmt: (v) => (v != null ? `#${v}` : '—') },
      { label: 'Vraag %', get: (p) => p.scores?.demand_pct, higher: true },
      { label: 'Marge %', get: (p) => p.scores?.margin_pct, higher: true },
      { label: 'Trend score', get: (p) => p.scores?.trend_score, higher: true },
      { label: 'Omloop %', get: (p) => p.scores?.turnover_pct, higher: true },
      { label: 'Voorraadrendement %', get: (p) => p.scores?.inventory_return_pct, higher: true },
      { label: 'Retour penalty', get: (p) => p.scores?.return_penalty, higher: false },
      { label: 'Verkocht 365d', get: (p) => p.metrics?.verkocht_365d, higher: true },
      { label: 'Jaarlijkse vraag', get: (p) => p.metrics?.jaarlijkse_vraag, higher: true },
      {
        label: 'Brutomarge 365d',
        get: (p) => p.metrics?.brutomarge_totaal_365d,
        higher: true,
        fmt: (v) =>
          v != null
            ? `€${Number(v).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`
            : '—',
      },
      {
        label: 'YoY trend',
        get: (p) => (p.metrics?.yoy_betrouwbaar ? p.metrics?.yoy_ratio : null),
        higher: true,
        fmt: (v) => (v != null ? `${Math.round(v * 100)}%` : 'n.v.t.'),
      },
      { label: 'Op voorraad', get: (p) => p.metrics?.huidige_voorraad, higher: true },
      { label: 'Onderweg', get: (p) => p.metrics?.voorraad_onderweg, higher: true },
      { label: 'In productie', get: (p) => p.metrics?.in_productie, higher: true },
      {
        label: 'Omloopsnelheid',
        get: (p) => p.metrics?.omloopsnelheid,
        higher: true,
        fmt: (v) => (v != null ? `${v}×/jr` : 'n.v.t.'),
      },
      {
        label: 'Fin. voorraadrendement',
        get: (p) => p.metrics?.voorraadrendement_financieel,
        higher: true,
        fmt: (v) => (v != null ? `${v}×` : 'n.v.t.'),
      },
      {
        label: 'Retour %',
        get: (p) => p.metrics?.pct_retour,
        higher: false,
        fmt: (v) => (v != null ? `${v}%` : 'n.v.t.'),
      },
    ],
    []
  );

  const renderRankingCompare = () => {
    const compared = rankingCompareData?.producten || [];

    return (
      <section className="product-ranking-compare">
        <div className="product-ranking-compare-head">
          <h3>
            <FiColumns aria-hidden /> Producten vergelijken
          </h3>
          <p>
            Voeg 2–{MAX_RANKING_COMPARE} producten toe (via zoeken of knop op een kaart) en vergelijk scores en
            onderliggende metrics.
          </p>
        </div>

        <div className="product-ranking-compare-picker">
          <div className="product-ranking-compare-search-wrap">
            <input
              type="search"
              className="product-ranking-compare-search"
              placeholder="Zoek product op naam of SKU…"
              value={rankingCompareQuery}
              onChange={(e) => setRankingCompareQuery(e.target.value)}
              autoComplete="off"
            />
            {rankingCompareSuggestions.length > 0 && (
              <ul className="product-ranking-compare-suggestions">
                {rankingCompareSuggestions.map((p) => (
                  <li key={p.id}>
                    <button type="button" onClick={() => addToRankingCompare(p)}>
                      <span>{p.naam}</span>
                      {p.sku ? <small>{p.sku}</small> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {rankingCompareItems.length > 0 ? (
            <div className="product-ranking-compare-chips">
              {rankingCompareItems.map((p) => (
                <span key={p.id} className="product-ranking-compare-chip">
                  <span className="product-ranking-compare-chip-label" title={p.naam}>
                    {p.naam}
                    {p.sku ? ` (${p.sku})` : ''}
                  </span>
                  <button
                    type="button"
                    className="product-ranking-compare-chip-remove"
                    onClick={() => removeFromRankingCompare(p.id)}
                    aria-label={`Verwijder ${p.naam}`}
                  >
                    <FiX />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="product-ranking-compare-empty">Nog geen producten geselecteerd.</p>
          )}

          <div className="product-ranking-compare-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={runRankingCompare}
              disabled={rankingCompareLoading || rankingCompareItems.length < 2}
            >
              {rankingCompareLoading ? 'Vergelijken…' : 'Vergelijk'}
            </button>
            {rankingCompareItems.length > 0 && (
              <button type="button" className="btn btn-secondary" onClick={clearRankingCompare}>
                Wis selectie
              </button>
            )}
          </div>
        </div>

        {compared.length >= 2 && (
          <div className="product-ranking-compare-table-wrap">
            <table className="product-ranking-compare-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  {compared.map((p) => (
                    <th key={p.product_id}>
                      <div className="product-ranking-compare-col-title">{p.naam}</div>
                      <div className="product-ranking-compare-col-meta">
                        {p.sku ? `SKU ${p.sku}` : ''}
                        {p.categorie ? ` · ${p.categorie}` : ''}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rankingCompareRows.map((row) => {
                  const rawValues = compared.map((p) => row.get(p));
                  const displayValues = rawValues.map((v) =>
                    row.fmt ? row.fmt(v) : v != null && v !== '' ? String(v) : '—'
                  );
                  return (
                    <tr key={row.label}>
                      <th scope="row">{row.label}</th>
                      {compared.map((p, idx) => (
                        <td
                          key={p.product_id}
                          className={rankingCompareCellClass(rawValues, idx, row.higher !== false)}
                        >
                          {displayValues[idx]}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rankingCompareData?.gewichten && (
              <p className="product-ranking-compare-footnote">
                Scores t.o.v. heel assortiment ({rankingCompareData.totaal_beoordeeld} producten).{' '}
                {rankingCompareData.gewichten}
              </p>
            )}
          </div>
        )}
      </section>
    );
  };

  const renderRankingTable = (items, variant = 'top') => {
    if (!items?.length) {
      const emptyMsg =
        variant === 'phase-out'
          ? 'Geen uit-faseer-kandidaten gevonden.'
          : 'Geen producten gevonden voor deze lijst.';
      return <div className="empty-state">{emptyMsg}</div>;
    }
    const accent = variant === 'top' ? '#0d9488' : '#dc2626';

    return (
      <div className="product-ranking-cards">
        {items.map((item, idx) => {
          const uitfaserenChecked =
            rankingUitfaserenLocal[item.product_id] ?? Number(item.uitfaseren) === 1;
          const uitfaserenSaving = Boolean(rankingUitfaserenBusy[item.product_id]);

          return (
          <article key={item.product_id} className="product-ranking-card">
            <div className="product-ranking-card-head">
              <span className="product-ranking-card-rank" style={{ color: accent }}>{idx + 1}</span>
              <div className="product-ranking-card-title">
                <ProductNaamMetKlasse productId={item.product_id} naam={item.naam} strong />
                <div className="product-ranking-card-meta">
                  {item.sku ? <span>SKU: {item.sku}</span> : null}
                  {item.categorie ? <span>{item.categorie}</span> : null}
                  {!rankingSupplierScope && item.leverancier_naam ? (
                    <span>{item.leverancier_naam}</span>
                  ) : null}
                  {item.in_proefperiode ? <span className="product-ranking-proef-badge">Proefperiode</span> : null}
                </div>
              </div>
              <div className="product-ranking-card-score" style={{ color: accent }}>
                <span className="product-ranking-card-score-label">Score</span>
                <strong>{item.score}</strong>
              </div>
              <label
                className={`product-ranking-uitfaseren${uitfaserenSaving ? ' is-saving' : ''}`}
                title="Uitfaseren — product wordt niet meer besteld"
              >
                <input
                  type="checkbox"
                  checked={uitfaserenChecked}
                  disabled={uitfaserenSaving}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleRankingUitfaserenToggle(item.product_id, e.target.checked);
                  }}
                />
                <span>{uitfaserenSaving ? 'Opslaan…' : 'Uitfaseren'}</span>
              </label>
              <button
                type="button"
                className="btn btn-secondary btn-sm product-ranking-card-action"
                onClick={() => navigate(`/producten/${item.product_id}`)}
              >
                <FiEye /> Bekijk
              </button>
              <button
                type="button"
                className={`btn btn-secondary btn-sm product-ranking-card-action product-ranking-compare-add${
                  rankingCompareSelectedIds.has(item.product_id) ? ' is-selected' : ''
                }`}
                onClick={() => {
                  if (rankingCompareSelectedIds.has(item.product_id)) {
                    removeFromRankingCompare(item.product_id);
                  } else {
                    addToRankingCompare(item);
                  }
                }}
                title="Toevoegen aan vergelijking"
              >
                <FiColumns aria-hidden /> Vergelijk
              </button>
            </div>

            <div className="product-ranking-card-stats">
              <div className="product-ranking-stat">
                <span>{percentileLabel}</span>
                <strong>{item.scores?.demand_pct ?? '—'}/{item.scores?.margin_pct ?? '—'}</strong>
              </div>
              <div className="product-ranking-stat">
                <span>Verkocht 365d</span>
                <strong>{item.metrics?.verkocht_365d ?? 0}</strong>
              </div>
              <div className="product-ranking-stat">
                <span>{(item.metrics?.verkocht_365d ?? 0) < 30 ? 'Jaarlijkse vraag (proj.)' : 'Jaarlijkse vraag'}</span>
                <strong>{item.metrics?.jaarlijkse_vraag ?? 0}</strong>
              </div>
              <div className="product-ranking-stat">
                <span>Brutomarge 365d</span>
                <strong>€{Number(item.metrics?.brutomarge_totaal_365d || 0).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
              </div>
              <div className="product-ranking-stat">
                <span>YoY trend</span>
                <strong className={item.metrics?.yoy_betrouwbaar && item.metrics?.yoy_ratio != null ? (item.metrics.yoy_ratio >= 1 ? 'stat-up' : 'stat-down') : ''}>
                  {item.metrics?.yoy_betrouwbaar && item.metrics?.yoy_ratio != null ? (
                    <>
                      {item.metrics.yoy_ratio >= 1 ? <FiTrendingUp /> : <FiTrendingDown />}
                      {' '}{Math.round(item.metrics.yoy_ratio * 100)}%
                    </>
                  ) : 'n.v.t.'}
                </strong>
              </div>
              <div className="product-ranking-stat">
                <span>Op voorraad</span>
                <strong>{item.metrics?.huidige_voorraad ?? 0}</strong>
              </div>
              <div className="product-ranking-stat">
                <span>Onderweg</span>
                <strong>{item.metrics?.voorraad_onderweg ?? 0}</strong>
              </div>
              <div className="product-ranking-stat">
                <span>In productie</span>
                <strong>{item.metrics?.in_productie ?? 0}</strong>
              </div>
              <div className="product-ranking-stat">
                <span>Omloopsnelheid</span>
                <strong>
                  {item.metrics?.omloopsnelheid != null ? `${item.metrics.omloopsnelheid}×/jr` : 'n.v.t.'}
                </strong>
                {item.metrics?.totaal_voorraad != null ? (
                  <span className="product-ranking-stat-sub">
                    {item.metrics.totaal_voorraad} stuks totaal
                  </span>
                ) : null}
              </div>
              <div className="product-ranking-stat">
                <span>Voorraadrendement</span>
                <strong>
                  {item.metrics?.voorraadrendement_financieel != null
                    ? `${item.metrics.voorraadrendement_financieel}×`
                    : 'n.v.t.'}
                </strong>
                {item.metrics?.voorraadwaarde_inkoop > 0 ? (
                  <span className="product-ranking-stat-sub">
                    €{Number(item.metrics.voorraadwaarde_inkoop).toLocaleString('nl-NL', { maximumFractionDigits: 0 })} voorraad (inkoop)
                  </span>
                ) : null}
              </div>
              <div className="product-ranking-stat product-ranking-stat-retour">
                <span>Retour %</span>
                <strong className={item.metrics?.pct_retour > 10 ? 'stat-down' : item.metrics?.pct_retour > 5 ? 'stat-warn' : ''}>
                  {item.metrics?.pct_retour != null ? `${item.metrics.pct_retour}%` : 'n.v.t.'}
                </strong>
                {item.metrics?.verkocht_retour_basis > 0 && (
                  <span className="product-ranking-stat-sub">
                    {item.metrics.retour_stuks ?? 0} / {item.metrics.verkocht_retour_basis} stuks
                  </span>
                )}
              </div>
            </div>

            {(item.redenen || []).length > 0 && (
              <div className="product-ranking-card-redenen">
                {(item.redenen || []).join(' · ')}
              </div>
            )}
          </article>
          );
        })}
      </div>
    );
  };

  const renderProductRankingLists = () => {
    if (!productRankingData) return null;

    if (productRankingData.weergave === 'per_categorie' && productRankingData.lijsten_per_categorie) {
      const entries = Object.entries(productRankingData.lijsten_per_categorie);
      if (!entries.length) {
        return <div className="empty-state">Geen categorieën gevonden voor deze selectie.</div>;
      }
      return (
        <div className="product-ranking-lists product-ranking-lists-per-category">
          {entries.map(([cat, lists]) => (
            <section key={cat} className="product-ranking-category-section">
              <h3 className="product-ranking-category-title">{cat}</h3>
              <div className="product-ranking-lists product-ranking-lists-inner">
                {showTopRankingList && (
                  <div>
                    <h4 className="product-ranking-list-heading product-ranking-list-heading-top">
                      {topListHeading}
                    </h4>
                    {renderRankingTable(lists.top_producten, 'top')}
                  </div>
                )}
                {showPhaseOutRankingList && lists.uit_te_faseren?.length > 0 && (
                  <div>
                    <h4 className="product-ranking-list-heading product-ranking-list-heading-phase">
                      {phaseOutHeading}
                    </h4>
                    {renderRankingTable(lists.uit_te_faseren, 'phase-out')}
                  </div>
                )}
              </div>
            </section>
          ))}
        </div>
      );
    }

    return (
      <div className="product-ranking-lists">
        {showTopRankingList && (
          <div>
            <h3 className="product-ranking-list-heading product-ranking-list-heading-top">
              {topListHeading}
            </h3>
            {renderRankingTable(productRankingData.top_producten, 'top')}
          </div>
        )}
        {showPhaseOutRankingList && productRankingData.uit_te_faseren?.length > 0 && (
          <div>
            <h3 className="product-ranking-list-heading product-ranking-list-heading-phase">
              {phaseOutHeading}
            </h3>
            {renderRankingTable(productRankingData.uit_te_faseren, 'phase-out')}
          </div>
        )}
      </div>
    );
  };

  const isNieuwVoorstelTab = !showUitverkoop && !showInkooporders && !showProductRanking;

  return (
    <div className={`inkoopvoorstellen ${showInkooporders ? 'view-inkooporders' : ''}`}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Inkoopvoorstellen</h1>
          <p className="page-subtitle">
            Automatische berekening van inkoopvoorstellen op basis van voorraad, verkoopdata en seizoensinvloeden
          </p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={handleShowUitverkoop}
            disabled={loadingUitverkoop}
          >
            <FiRefreshCw className={loadingUitverkoop ? 'spinning' : ''} />
            {loadingUitverkoop ? 'Berekenen...' : 'Uitverkoop Overzicht'}
          </button>
        </div>
      </div>

      {searchTerm && isNieuwVoorstelTab && (
        <div className="search-results-info" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF' }}>
          Zoekresultaten voor "{searchTerm}"
        </div>
      )}

      {/* Tabs */}
      {/* Tabs: Nieuw Voorstel, Inkooporders, Uitverkoop */}
      <div className="tabs">
        <button
          className={`tab ${isNieuwVoorstelTab ? 'active' : ''}`}
          onClick={() => {
            setSavedProposals(null);
            setShowUitverkoop(false);
            setShowInkooporders(false);
            setShowProductRanking(false);
            // selectedLeverancier en proposals NIET resetten — behoudt de keuze
          }}
        >
          Nieuw Voorstel
        </button>
        <button
          className={`tab ${showInkooporders ? 'active' : ''}`}
          onClick={() => {
            setShowInkooporders(true);
            setSavedProposals(null);
            setShowUitverkoop(false);
            setShowProductRanking(false);
            fetchInkooporders();
            fetchProducten();
            setCollapsedSuppliers({});
          }}
        >
          <FiShoppingCart /> Inkooporders
          {inkooporders.length > 0 && (
            <span className="tab-badge">{inkooporders.length}</span>
          )}
        </button>
        <button
          className={`tab ${showUitverkoop ? 'active' : ''}`}
          onClick={() => {
            handleShowUitverkoop();
            setShowInkooporders(false);
            setShowProductRanking(false);
            setSavedProposals(null);
            setCollapsedSuppliers({});
          }}
        >
          Uitverkoop Overzicht
          {uitverkoopData && uitverkoopData.samenvatting && (
            <span className="tab-badge" style={{ background: uitverkoopData.samenvatting.kritiek > 0 ? '#ff4444' : '#3FDFCF' }}>
              {uitverkoopData.samenvatting.kritiek + uitverkoopData.samenvatting.laag}
            </span>
          )}
        </button>
        <button
          className={`tab ${showProductRanking ? 'active' : ''}`}
          onClick={() => {
            handleShowProductRanking();
            setCollapsedSuppliers({});
          }}
        >
          Productranking
          {productRankingData && (
            <span className="tab-badge" style={{ background: '#0d9488' }}>
              {productRankingData.top_producten?.length || 0}
            </span>
          )}
        </button>
      </div>

      <div className="inkoopvoorstellen-content">
      {/* Productranking */}
      {showProductRanking && (
        <div className="proposals-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              <h2 style={{ margin: 0, color: '#333' }}>Productranking</h2>
              <p style={{ margin: '0.35rem 0 0', color: '#666', fontSize: '0.9rem' }}>
                Stel filters in voor categorie, aantal, lijsttype en meer. Gebaseerd op vraag, marge, YoY-trend, retour%, plus licht gewicht voor voorraadomloop en financieel voorraadrendement.
              </p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleApplyRankingFilters} disabled={loadingProductRanking}>
              <FiRefreshCw className={loadingProductRanking ? 'spinning' : ''} />
              {loadingProductRanking ? 'Berekenen…' : 'Vernieuwen'}
            </button>
          </div>

          <div className="product-ranking-filters">
            <div className="product-ranking-filters-row">
              <label className="product-ranking-filter">
                <span>Aantal</span>
                <select
                  value={rankingFilters.limit}
                  onChange={(e) => setRankingFilters((prev) => ({ ...prev, limit: parseInt(e.target.value, 10) || 10 }))}
                >
                  {[5, 10, 20, 30, 50].map((n) => (
                    <option key={n} value={n}>Top {n}</option>
                  ))}
                </select>
              </label>

              <label className="product-ranking-filter">
                <span>Lijst</span>
                <select
                  value={rankingFilters.lijst}
                  onChange={(e) => setRankingFilters((prev) => ({ ...prev, lijst: e.target.value }))}
                >
                  <option value="beide">Top + uit te faseren</option>
                  <option value="top">Alleen top performers</option>
                  <option value="uitfaseren">Alleen uit te faseren</option>
                </select>
              </label>

              <label className="product-ranking-filter">
                <span>Weergave</span>
                <select
                  value={rankingFilters.weergave}
                  onChange={(e) => setRankingFilters((prev) => ({ ...prev, weergave: e.target.value }))}
                >
                  <option value="gecombineerd">Gecombineerd</option>
                  <option value="per_categorie">Per categorie</option>
                </select>
              </label>

              <label className="product-ranking-filter">
                <span>Klasse</span>
                <select
                  value={rankingFilters.klasse}
                  onChange={(e) => setRankingFilters((prev) => ({ ...prev, klasse: e.target.value }))}
                >
                  <option value="">Alle klassen</option>
                  <option value="top">Top</option>
                  <option value="prima">Prima</option>
                  <option value="matig">Matig</option>
                  <option value="proef">Proefperiode</option>
                </select>
              </label>

              <label className="product-ranking-filter">
                <span>Leverancier</span>
                <select
                  value={rankingFilters.leverancier_id}
                  onChange={(e) => setRankingFilters((prev) => ({ ...prev, leverancier_id: e.target.value }))}
                >
                  <option value="">Alle leveranciers</option>
                  {leveranciers.map((lev) => (
                    <option key={lev.id} value={lev.id}>{lev.naam}</option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                className="btn btn-primary product-ranking-apply-btn"
                onClick={handleApplyRankingFilters}
                disabled={loadingProductRanking}
              >
                Toepassen
              </button>
            </div>

            <div className="product-ranking-categories">
              <div className="product-ranking-categories-head">
                <span>Categorieën</span>
                <span className="product-ranking-categories-hint">
                  {rankingFilters.categorien.length
                    ? `${rankingFilters.categorien.length} geselecteerd`
                    : 'Geen selectie = alle categorieën'}
                  {rankingFilters.weergave === 'per_categorie' && !rankingFilters.categorien.length
                    ? ' · per categorie toont max. 8 grootste categorieën'
                    : ''}
                </span>
                {rankingFilters.categorien.length > 0 && (
                  <button type="button" className="product-ranking-clear-cats" onClick={clearRankingCategories}>
                    Wis selectie
                  </button>
                )}
              </div>
              {rankingCategoryOptions.length > 0 ? (
                <div className="product-ranking-category-grid">
                  {rankingCategoryOptions.map((cat) => {
                    const checked = rankingFilters.categorien.includes(cat.naam);
                    return (
                      <label key={cat.naam} className={`product-ranking-category-chip ${checked ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRankingCategory(cat.naam)}
                        />
                        <span>{cat.naam}</span>
                        <small>({cat.aantal})</small>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="product-ranking-categories-empty">
                  Open of vernieuw de ranking om categorieën te laden.
                </div>
              )}
            </div>
          </div>

          {renderRankingCompare()}

          {loadingProductRanking && !productRankingData ? (
            <div className="empty-state">Productranking berekenen…</div>
          ) : productRankingData ? (
            <>
              {productRankingData.uitleg && (
                <details className="collapsible-uitleg product-ranking-uitleg">
                  <summary className="collapsible-uitleg-summary">
                    Methodiek &amp; klassen · berekend {productRankingData.berekend_op} · {productRankingData.totaal_beoordeeld} producten
                  </summary>
                  <div className="collapsible-uitleg-body" style={{ fontSize: '0.85rem', color: '#555' }}>
                    <p style={{ margin: '0 0 0.5rem' }}>
                      <strong>Methodiek:</strong> {productRankingData.uitleg.volume} {productRankingData.uitleg.trend}{' '}
                      {productRankingData.uitleg.marge} {productRankingData.uitleg.voorraad} {productRankingData.uitleg.retour}
                    </p>
                    {productRankingData.uitleg.gewichten && (
                      <p style={{ margin: '0 0 0.5rem' }}>
                        <strong>Scoregewichten:</strong> {productRankingData.uitleg.gewichten}
                      </p>
                    )}
                    {productRankingData.uitleg.categorie && (
                      <p style={{ margin: '0 0 0.5rem' }}>
                        <strong>Percentielen:</strong> {productRankingData.uitleg.categorie}
                      </p>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                      {Object.entries(PRODUCT_KLASSE_META).map(([key, meta]) => (
                        <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <ProductKlasseIcon klasse={key} size={14} />
                          {meta.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </details>
              )}

              {renderProductRankingLists()}
            </>
          ) : (
            <div className="empty-state">Klik op Productranking om te berekenen</div>
          )}
        </div>
      )}

      {/* Uitverkoop Overzicht */}
      {showUitverkoop && (
        <div className="proposals-container">
          {loadingUitverkoop ? (
            <div className="empty-state">Berekenen uitverkoopdata...</div>
          ) : filteredUitverkoopData ? (
            <>
              {searchTerm && (
                <div className="search-results-info" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF' }}>
                  <strong>{filteredUitverkoopData.producten.length}</strong> van <strong>{uitverkoopData?.producten?.length || 0}</strong> product(en) gevonden voor "{searchTerm}"
                </div>
              )}

              {filteredUitverkoopData.samenvatting && (
                <div className="uitverkoop-summary">
                  <div className="summary-item">
                    <strong style={{ color: '#ff4444' }}>{filteredUitverkoopData.samenvatting.kritiek}</strong>
                    <span>Kritiek (&lt;7 dagen)</span>
                  </div>
                  <div className="summary-item">
                    <strong style={{ color: '#ff8800' }}>{filteredUitverkoopData.samenvatting.laag}</strong>
                    <span>Laag (7-14 dagen)</span>
                  </div>
                  <div className="summary-item">
                    <strong style={{ color: '#ffaa00' }}>{filteredUitverkoopData.samenvatting.waarschuwing}</strong>
                    <span>Waarschuwing (14-30 dagen)</span>
                  </div>
                  <div className="summary-item">
                    <strong style={{ color: '#3FDFCF' }}>{filteredUitverkoopData.samenvatting.voldoende}</strong>
                    <span>Voldoende (&gt;30 dagen)</span>
                  </div>
                  <div className="summary-item">
                    <strong style={{ color: '#999' }}>{filteredUitverkoopData.samenvatting.uitverkocht}</strong>
                    <span>Uitverkocht</span>
                  </div>
                </div>
              )}

              <div className="uitverkoop-mobile-sort-bar">
                <label className="uitverkoop-mobile-sort-label" htmlFor="uitverkoop-sort-col-mobile">
                  Volgorde
                </label>
                <select
                  id="uitverkoop-sort-col-mobile"
                  aria-label="Sorteer op kolom"
                  value={uitverkoopSortColumn || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setUitverkoopSortColumn(null);
                      return;
                    }
                    setUitverkoopSortColumn(v);
                    setUitverkoopSortDirection('asc');
                  }}
                >
                  <option value="">Standaard</option>
                  {uitverkoopColumns.map((col) => (
                    <option key={col.key} value={col.key}>
                      {col.label}
                    </option>
                  ))}
                </select>
                <select
                  id="uitverkoop-sort-dir-mobile"
                  aria-label="Sorteerrichting"
                  value={uitverkoopSortDirection}
                  disabled={!uitverkoopSortColumn}
                  onChange={(e) => setUitverkoopSortDirection(e.target.value)}
                >
                  <option value="asc">{uitverkoopMobileDirLabels.asc}</option>
                  <option value="desc">{uitverkoopMobileDirLabels.desc}</option>
                </select>
              </div>
              
              <div className="uitverkoop-mobile-list">
                {sortedUitverkoopProducten.length === 0 ? (
                  <div className="empty-state">
                    {searchTerm ? `Geen producten gevonden voor "${searchTerm}"` : 'Geen producten gevonden'}
                  </div>
                ) : (
                  sortedUitverkoopProducten.map((product) => {
                    const statusMeta = getUitverkoopStatusMeta(product.status);
                    return (
                      <div key={`uitverkoop-mobile-${product.product_id}`} className="mobile-data-card mobile-uitverkoop-compact">
                        <div className="mobile-data-card-top">
                          <div>
                            <h4><ProductNaamMetKlasse productId={product.product_id} naam={product.product_naam} /></h4>
                            <p>{product.product_sku || '-'} · {product.leverancier_naam || '-'}</p>
                          </div>
                          <span
                            className="status-badge"
                            style={{ background: `${statusMeta.color}20`, color: statusMeta.color }}
                          >
                            {statusMeta.label}
                          </span>
                        </div>
                        <div className="mobile-data-grid mobile-uitverkoop-grid-compact">
                          <div><span>Voorraad</span><strong>{product.huidige_voorraad}</strong></div>
                          <div><span>Onderweg</span><strong>{product.voorraad_onderweg || 0}</strong></div>
                          <div><span>In prod.</span><strong>{product.in_productie || 0}</strong></div>
                          <div className="mobile-uitverkoop-bestelmoment-cell">
                            <span>Bestelmoment</span>
                            <strong>
                              {product.dagen_tot_bestelmoment !== null && product.dagen_tot_bestelmoment !== undefined
                                ? (product.dagen_tot_bestelmoment > 0 ? `${product.dagen_tot_bestelmoment}d` : 'Nu!')
                                : '-'}
                            </strong>
                          </div>
                        </div>
                        {product.dagen_tot_bestelmoment < 0 ? (
                          <div className="mobile-card-note" style={{ color: '#ff4444' }}>
                            {Math.abs(product.dagen_tot_bestelmoment)} dagen te laat · uitverkoop over {product.dagen_tot_uitverkoop} dagen
                          </div>
                        ) : product.dagen_tot_uitverkoop !== null && product.dagen_tot_uitverkoop !== undefined ? (
                          <div className="mobile-card-note">
                            Uitverkoop over {product.dagen_tot_uitverkoop} dagen
                          </div>
                        ) : null}
                        <div className="mobile-data-card-actions mobile-uitverkoop-actions">
                          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/producten/${product.product_id}`)}>
                            <FiEye /> Product openen
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={() => handleShowUitverkoopCalculation(product.product_id)}>
                            <FiInfo /> Berekening
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="uitverkoop-table-container uitverkoop-desktop-table">
                <table className="proposal-table uitverkoop-sortable-table">
                  <colgroup>
                    {uitverkoopColumnWidths.map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {uitverkoopColumns.map((col, i) => (
                        <th
                          key={col.key}
                          title={col.label}
                          className="uitverkoop-th-sortable"
                          onClick={() => handleUitverkoopSort(col.key)}
                          style={{ position: 'relative', cursor: 'pointer', userSelect: 'none' }}
                        >
                          <span className="uitverkoop-th-text">{col.label}</span>
                          {uitverkoopSortColumn === col.key && (
                            <span className="uitverkoop-sort-icon" aria-hidden="true">
                              {uitverkoopSortDirection === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                          {i < uitverkoopColumns.length - 1 && (
                            <span
                              className="uitverkoop-resize-handle"
                              title="Sleep om kolom te verbreeden of te versmallen"
                              onMouseDown={(e) => handleUitverkoopResizeStart(i, e)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUitverkoopProducten.length === 0 ? (
                      <tr>
                        <td colSpan="14" style={{ textAlign: 'center', padding: '2rem' }}>
                          {searchTerm ? `Geen producten gevonden voor "${searchTerm}"` : 'Geen producten gevonden'}
                        </td>
                      </tr>
                    ) : (
                      sortedUitverkoopProducten.map((product) => {
                        const getStatusColor = (status) => {
                          switch(status) {
                            case 'uitverkocht': return '#999';
                            case 'kritiek': return '#ff4444';
                            case 'laag': return '#ff8800';
                            case 'waarschuwing': return '#ffaa00';
                            case 'onder_minimum': return '#ff4444';
                            case 'onvoldoende_data': return '#999';
                            default: return '#3FDFCF';
                          }
                        };
                        
                        const getStatusLabel = (status) => {
                          switch(status) {
                            case 'uitverkocht': return 'Uitverkocht';
                            case 'kritiek': return 'Kritiek';
                            case 'laag': return 'Laag';
                            case 'waarschuwing': return 'Waarschuwing';
                            case 'onder_minimum': return 'Onder Minimum';
                            case 'onvoldoende_data': return 'Onvoldoende Data';
                            default: return 'Voldoende';
                          }
                        };
                        
                        return (
                          <tr key={product.product_id}>
                            <td><ProductNaamMetKlasse productId={product.product_id} naam={product.product_naam} strong /></td>
                            <td>{product.product_sku || '-'}</td>
                            <td>{product.leverancier_naam}</td>
                            <td>{product.huidige_voorraad}</td>
                            <td>{product.voorraad_onderweg || 0}</td>
                            <td>{product.in_productie || 0}</td>
                            <td><strong>{product.totaal_voorraad}</strong></td>
                            <td>{product.minimum_voorraad || 0}</td>
                            <td>{product.dagelijkse_verkoop ? product.dagelijkse_verkoop.toFixed(2) : '0.00'}</td>
                            <td>{product.verwachte_verkoop_per_maand || 0}</td>
                            <td style={{ textAlign: 'right' }} title="Aantal keer per jaar dat voorraad omdraait">
                              {product.omloopsnelheid != null ? `${product.omloopsnelheid}×/jr` : '-'}
                            </td>
                            <td>
                              {product.uitverkoopdatum ? (
                                <strong style={{ color: getStatusColor(product.status) }}>
                                  {new Date(product.uitverkoopdatum).toLocaleDateString('nl-NL')}
                                </strong>
                              ) : product.status === 'onvoldoende_data' ? (
                                <span style={{ color: '#999' }} title="Te weinig verkoopdata voor betrouwbare berekening">Onvoldoende data</span>
                              ) : (
                                <span style={{ color: '#999' }}>Geen data</span>
                              )}
                            </td>
                            <td>
                              {product.dagen_tot_bestelmoment !== null && product.dagen_tot_bestelmoment !== undefined ? (
                                <div>
                                  <strong style={{ color: getStatusColor(product.status) }}>
                                    {product.dagen_tot_bestelmoment > 0 ? `${product.dagen_tot_bestelmoment} dagen` : 'Nu bestellen!'}
                                  </strong>
                                  {product.dagen_tot_bestelmoment < 0 && (
                                    <div style={{ fontSize: '0.8em', color: '#ff4444', marginTop: '0.25rem' }}>
                                      ({Math.abs(product.dagen_tot_bestelmoment)} dagen te laat)
                                    </div>
                                  )}
                                  <div style={{ fontSize: '0.75em', color: '#999', marginTop: '0.25rem' }}>
                                    (uitverkoop: {product.dagen_tot_uitverkoop} dagen)
                                  </div>
                                </div>
                              ) : product.dagen_tot_uitverkoop !== null ? (
                                <strong style={{ color: getStatusColor(product.status) }}>
                                  {product.dagen_tot_uitverkoop} dagen
                                </strong>
                              ) : product.status === 'onvoldoende_data' ? (
                                <span style={{ color: '#999' }} title="Minimaal 15 stuks verkoop nodig voor berekening">-</span>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span 
                                  className="status-badge" 
                                  style={{ 
                                    background: getStatusColor(product.status) + '20',
                                    color: getStatusColor(product.status)
                                  }}
                                >
                                  {getStatusLabel(product.status)}
                                </span>
                                <button
                                  className="btn-icon"
                                  onClick={() => handleShowUitverkoopCalculation(product.product_id)}
                                  title="Toon berekening"
                                  style={{ 
                                    padding: '0.25rem', 
                                    background: 'transparent', 
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontSize: '1.1rem'
                                  }}
                                >
                                  <FiInfo />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="empty-state">Klik op "Uitverkoop Overzicht" om te berekenen</div>
          )}
        </div>
      )}

      {/* Nieuw voorstel: leverancierslijst + CBM; tonen als er geen opgeslagen voorstellen zijn of op Nieuw-voorstel-tab */}
      {(!savedProposals || !savedProposals.per_leverancier?.length) && isNieuwVoorstelTab && (
        <div className="proposals-container">
          {/* Leveranciers standaard getoond met CBM-signaal */}
          <div className="compact-card" style={{ 
            background: 'white', 
            padding: '0.75rem 1rem', 
            borderRadius: '8px', 
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            marginBottom: '0.5rem'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#333', fontSize: '1.1rem' }}>Nieuw Inkoopvoorstel</h2>
            <p style={{ color: '#666', marginBottom: '0.75rem', fontSize: '0.85rem' }}>
              {proposals
                ? 'Overzicht CBM voor het berekende voorstel. Je kunt per leverancier opnieuw berekenen of alles tegelijk doen.'
                : 'Bereken een voorstel voor alle leveranciers of alleen voor de leverancier die je wilt bekijken.'}
            </p>
            <div className="nieuw-voorstel-controls" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <select
                value={selectedLeverancier}
                onChange={(e) => setSelectedLeverancier(e.target.value)}
                style={{ minWidth: '240px', padding: '0.6rem', borderRadius: '6px', border: '1px solid #ddd', fontFamily: 'inherit' }}
              >
                <option value="">Alle leveranciers</option>
                {leveranciers.map((lev) => (
                  <option key={lev.id} value={lev.id}>{lev.naam}</option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                onClick={() => handleCalculate(selectedLeverancier || null)}
                disabled={calculating}
              >
                {calculating
                  ? 'Berekenen...'
                  : selectedLeverancier
                    ? 'Bereken gekozen leverancier'
                    : 'Bereken alle leveranciers'}
              </button>
            </div>
            <ul className="leverancier-select-list" style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem 0', border: '1px solid #eee', borderRadius: '6px', overflow: 'hidden' }}>
              {leveranciers.length === 0 ? (
                <li style={{ padding: '0.75rem', color: '#666' }}>Geen leveranciers gevonden. Voeg leveranciers toe om voorstellen te berekenen.</li>
              ) : leveranciers.map(lev => (
                <li
                  key={lev.id}
                  className="leverancier-select-row"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    borderBottom: '1px solid #eee',
                    background: (cbmPerLeverancier[lev.id] || 0) > 0 ? 'rgba(63, 223, 207, 0.08)' : 'transparent'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: 0 }}>
                    <span style={{ fontWeight: 500 }}>{lev.naam}</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', color: (cbmPerLeverancier[lev.id] || 0) > 0 ? '#0a7c6e' : '#666', fontSize: '0.9rem' }}>
                      {proposals
                        ? `${(cbmPerLeverancier[lev.id] ?? 0).toFixed(3)} CBM`
                        : '– CBM'}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleCalculate(String(lev.id))}
                    disabled={calculating}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {calculating && String(selectedLeverancier) === String(lev.id) ? 'Berekenen...' : 'Bereken'}
                  </button>
                </li>
              ))}
            </ul>
            {proposals ? (
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary" onClick={() => handleCalculate(null)} disabled={calculating}>
                  {calculating ? 'Berekenen...' : 'Bereken opnieuw voor alle leveranciers'}
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  <FiSave className={saving ? 'spinning' : ''} />
                  {saving ? 'Opslaan...' : 'Opslaan'}
                </button>
              </div>
            ) : null}
          </div>
          {proposals && (
            <>
              <div style={{ 
                background: 'white', 
                padding: '0.5rem 0.75rem', 
                borderRadius: '8px', 
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                marginBottom: '0.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.5rem'
              }}>
                <div>
                  <h3 style={{ margin: 0, marginBottom: '0.25rem' }}>Inkoopvoorstel Overzicht</h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                    {selectedLeverancier
                      ? `${leveranciers.find((l) => String(l.id) === String(selectedLeverancier))?.naam || 'Gekozen leverancier'} — totaal ${proposals.totaal_leveranciers || 0} leverancier(s)`
                      : `Alle leveranciers — totaal ${proposals.totaal_leveranciers || 0} leverancier(s)`}
                  </p>
                </div>
              </div>
              <div className="proposal-summary">
                <div className="summary-item">
                  <strong>{proposals.totaal_producten || 0}</strong>
                  <span>Producten</span>
                </div>
                <div className="summary-item">
                  <strong>{proposals.totaal_leveranciers || 0}</strong>
                  <span>Leveranciers</span>
                </div>
                <div className="summary-item">
                  <strong>{proposals.totaal_voorgesteld_aantal || 0}</strong>
                  <span>Totaal Voorgesteld</span>
                </div>
              </div>
              {renderProposals(proposals, false)}
              {grandTotalCBMProposals > 0 && (
                <div className="proposal-grand-total-cbm" style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF', fontWeight: 'bold', textAlign: 'right' }}>
                  Totaal CBM (totaal overzicht): {grandTotalCBMProposals.toFixed(3)}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Opgeslagen voorstellen - alleen tonen als er echt opgeslagen voorstellen zijn */}
      {savedProposals && savedProposals.per_leverancier?.length > 0 && isNieuwVoorstelTab && (
        <div className="proposals-container">
          <div style={{
              background: '#e3f2fd',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #2196f3',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div>
                <strong>Opgeslagen voorstellen omzetten naar inkooporders</strong>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem', color: '#555' }}>
                  Zet alle opgeslagen voorstellen om naar echte inkooporders. Daarna zie je ze onder het tabblad Inkoop orders.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={handleConvertSavedToInkooporders}
                >
                  <FiCheckCircle /> Maak inkooporders
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ color: '#c62828', borderColor: '#c62828' }}
                  onClick={async () => {
                    const alleIds = savedProposals.per_leverancier
                      .flatMap(lev => (lev.producten || []).map(p => p.id).filter(Boolean));
                    if (alleIds.length === 0) return;
                    if (!window.confirm(`Alle ${alleIds.length} voorstelregel(s) verwijderen?`)) return;
                    for (const id of alleIds) {
                      await api.delete(`/inkoopvoorstellen/${id}`);
                    }
                    fetchSavedProposals();
                  }}
                >
                  <FiTrash2 /> Alles verwijderen
                </button>
              </div>
            </div>
          {definitiefCount > 0 && (
            <div className="definitief-actions" style={{
              background: '#fff4e6',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '2px solid #ffa726',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
              <div>
                <strong>{definitiefCount} definitief voorstel(len)</strong> klaar om om te zetten naar orders
              </div>
              <button
                className="btn btn-primary"
                onClick={handleConvertToInkooporders}
              >
                <FiCheckCircle /> Omzetten naar Inkooporders
              </button>
            </div>
          )}

          {selectedProposals.size > 0 && (
            <div className="bulk-actions" style={{
              background: '#f0fffe',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #3FDFCF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}>
              <div>
                <strong>{selectedProposals.size} voorstel(len) geselecteerd</strong>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => handleMarkDefinitief(Array.from(selectedProposals))}
              >
                <FiCheckCircle /> Markeer als Definitief
              </button>
            </div>
          )}

          {renderProposals(savedProposals, true)}
          {grandTotalCBMSaved > 0 && (
            <div className="proposal-grand-total-cbm" style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF', fontWeight: 'bold', textAlign: 'right' }}>
              Totaal CBM (totaal overzicht): {grandTotalCBMSaved.toFixed(3)}
            </div>
          )}
        </div>
      )}

      </div>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingProposal(null);
        }}
        title={`Voorstel Bewerken: ${editingProposal?.product_naam}`}
      >
        {editingProposal && (
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            handleUpdateProposal(editingProposal.id, {
              aangepast_aantal: parseInt(formData.get('aangepast_aantal')) || null,
              opmerkingen: formData.get('opmerkingen') || null
            });
          }}>
            <div className="form-group">
              <label>Voorgesteld Aantal</label>
              <input
                type="number"
                value={editingProposal.voorgesteld_aantal || 0}
                disabled
                style={{ background: '#f5f5f5' }}
              />
            </div>

            <div className="form-group">
              <label>Aangepast Aantal *</label>
              <input
                type="number"
                name="aangepast_aantal"
                min="0"
                defaultValue={editingProposal.aangepast_aantal || editingProposal.voorgesteld_aantal || 0}
                required
              />
              <small>Pas het aantal aan indien nodig</small>
            </div>

            <div className="form-group">
              <label>Opmerkingen</label>
              <textarea
                name="opmerkingen"
                rows="3"
                defaultValue={editingProposal.opmerkingen || ''}
                placeholder="Optionele opmerkingen..."
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingProposal(null);
                }}
              >
                Annuleren
              </button>
              <button type="submit" className="btn btn-primary">
                <FiSave /> Opslaan
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Berekening Modal */}
      <Modal
        show={showCalculationModal}
        onClose={() => {
          setShowCalculationModal(false);
          setCalculationData(null);
        }}
        title="Berekening Details"
      >
        {loadingCalculation ? (
          <div className="loading">Berekening ophalen...</div>
        ) : calculationData ? (
          <div style={{ padding: '1rem' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>
                <ProductNaamMetKlasse
                  productId={calculationData.product?.id}
                  naam={calculationData.product?.naam || calculationData.product?.product_naam}
                />
              </h3>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>SKU: {calculationData.product?.sku || calculationData.product?.product_sku}</p>
              {calculationData.product?.leverancier && (
                <p style={{ color: '#666', fontSize: '0.9rem' }}>Leverancier: {calculationData.product.leverancier}</p>
              )}
            </div>

            {/* Uitverkoop berekening (nieuwe structuur) */}
            {calculationData.berekening_stap_voor_stap ? (
              <>
                {calculationData.voorraad && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem' }}>Voorraad</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <div>Huidige voorraad: <strong>{calculationData.voorraad.huidige_voorraad}</strong></div>
                      <div>Onderweg: <strong>{calculationData.voorraad.voorraad_onderweg}</strong></div>
                      {(calculationData.voorraad.voorraad_onderweg_kolom != null &&
                        calculationData.voorraad.voorraad_onderweg_kolom !== calculationData.voorraad.voorraad_onderweg_uit_orders) && (
                        <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: '#b45309' }}>
                          DB-kolom had {calculationData.voorraad.voorraad_onderweg_kolom}; geteld uit inkooporders: {calculationData.voorraad.voorraad_onderweg_uit_orders}
                        </div>
                      )}
                      <div>In Productie: <strong>{calculationData.voorraad.in_productie || 0}</strong></div>
                      <div>Totaal: <strong>{calculationData.voorraad.totaal_voorraad}</strong></div>
                      <div>Minimum: <strong>{calculationData.voorraad.minimum_voorraad}</strong></div>
                    </div>
                    {(calculationData.voorraad.in_productie || 0) > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <button type="button" className="btn btn-small btn-secondary" disabled={correctingInProductie} onClick={handleZetInProductieOpNul}>
                          {correctingInProductie ? 'Bezig…' : "Zet 'In productie' op 0 (correctie)"}
                        </button>
                        <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '0.5rem' }}>Als er feitelijk niets in productie staat</span>
                      </div>
                    )}
                  </div>
                )}

                <h4 style={{ marginBottom: '1rem', color: '#333', borderBottom: '2px solid #3FDFCF', paddingBottom: '0.5rem' }}>
                  Stap-voor-stap Berekening
                </h4>
                
                {/* Stap 1: Historische verkoop */}
                {calculationData.historische_data && calculationData.berekening_stap_voor_stap?.stap_1_historische_verkoop && (
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                    <strong style={{ color: '#3FDFCF' }}>Stap 1: Historische Verkoop</strong>
                    <div style={{ marginTop: '0.5rem' }}>
                      <div>Totaal verkocht (365 dagen): <strong>{calculationData.historische_data.totaal_verkocht_365_dagen} stuks</strong></div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        {calculationData.berekening_stap_voor_stap.stap_1_historische_verkoop.formule}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Stap 2: Maandelijks gemiddelde */}
                <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <strong style={{ color: '#3FDFCF' }}>Stap 2: Maandelijks Gemiddelde</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <div>Gemiddeld per maand: <strong>{calculationData.berekening_stap_voor_stap.stap_2_maandelijks_gemiddelde.waarde} stuks/maand</strong></div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                      {calculationData.berekening_stap_voor_stap.stap_2_maandelijks_gemiddelde.formule}
                    </div>
                  </div>
                </div>
                
                {/* Stap 3: Met groei */}
                <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <strong style={{ color: '#3FDFCF' }}>Stap 3: Met Groeifactor</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <div>Verkoopgroei: <strong>{calculationData.berekening_stap_voor_stap.stap_3_met_groei.verkoopgroei_percentage}%</strong></div>
                    <div>Verwachte verkoop: <strong>{calculationData.berekening_stap_voor_stap.stap_3_met_groei.waarde} stuks/maand</strong></div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                      {calculationData.berekening_stap_voor_stap.stap_3_met_groei.formule}
                    </div>
                  </div>
                </div>
                
                {/* Stap 4: Met seizoen (vooruitkijkend) */}
                <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <strong style={{ color: '#3FDFCF' }}>Stap 4: Seizoensfactoren vooruit</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <div>Huidige maand: <strong>{calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.seizoensfactor_huidige_maand ?? calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.seizoensfactor}</strong></div>
                    {calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.seizoensfactor_gemiddeld_periode != null && (
                      <div>Gemiddeld over periode: <strong>{calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.seizoensfactor_gemiddeld_periode}</strong></div>
                    )}
                    <div>Verwachte verkoop equivalent: <strong>{calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.waarde} stuks/maand</strong></div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                      {calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.formule}
                    </div>
                    {Array.isArray(calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.seizoen_vooruit) && calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.seizoen_vooruit.length > 0 && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                        {calculationData.berekening_stap_voor_stap.stap_4_met_seizoen.seizoen_vooruit.map((seg, i) => (
                          <div key={i}>
                            {seg.fase === 'levertijd' ? 'Levertijd' : 'Buffer ná binnenkomst'} · {seg.maand_naam} {seg.jaar}: {seg.dagen}d × factor {seg.seizoensfactor} ≈ {seg.verwachte_verkoop} stuks
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Stap 5: Voorraad bij binnenkomst */}
                {calculationData.berekening_stap_voor_stap.stap_5_voorraad_behoefte && (
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: '#fff8e1', borderRadius: '8px', border: '1px solid #ffc107' }}>
                    <strong style={{ color: '#333' }}>Stap 5: Voorraad bij binnenkomst</strong>
                    <div style={{ marginTop: '0.5rem' }}>
                      <div>Verkoop tijdens levertijd: <strong>{calculationData.berekening_stap_voor_stap.stap_5_voorraad_behoefte.verkoop_tijdens_levertijd} stuks</strong></div>
                      <div>Verkoop buffer ná binnenkomst ({calculationData.berekening_stap_voor_stap.stap_5_voorraad_behoefte.aankomst_datum}): <strong>{calculationData.berekening_stap_voor_stap.stap_5_voorraad_behoefte.verkoop_na_binnenkomst} stuks</strong></div>
                      <div>Benodigde voorraad totaal: <strong>{calculationData.berekening_stap_voor_stap.stap_5_voorraad_behoefte.benodigde_voorraad_totaal} stuks</strong></div>
                      <div>Voorraad bij binnenkomst (projectie): <strong>{calculationData.berekening_stap_voor_stap.stap_5_voorraad_behoefte.voorraad_bij_binnenkomst} stuks</strong></div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        {calculationData.berekening_stap_voor_stap.stap_5_voorraad_behoefte.uitleg}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Stap 6 / 5: Dagelijkse verkoop */}
                <div style={{ marginBottom: '1rem', padding: '1rem', background: '#e8f5e9', borderRadius: '8px', border: '2px solid #3FDFCF' }}>
                  <strong style={{ color: '#3FDFCF', fontSize: '1.1rem' }}>{calculationData.berekening_stap_voor_stap.stap_6_dagelijkse_verkoop ? 'Stap 6: Dagelijkse Verkoop' : 'Stap 5: Dagelijkse Verkoop'}</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#333' }}>
                      {(calculationData.berekening_stap_voor_stap.stap_6_dagelijkse_verkoop || calculationData.berekening_stap_voor_stap.stap_5_dagelijkse_verkoop).waarde} stuks/dag
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                      {(calculationData.berekening_stap_voor_stap.stap_6_dagelijkse_verkoop || calculationData.berekening_stap_voor_stap.stap_5_dagelijkse_verkoop).formule}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem', fontStyle: 'italic' }}>
                      {(calculationData.berekening_stap_voor_stap.stap_6_dagelijkse_verkoop || calculationData.berekening_stap_voor_stap.stap_5_dagelijkse_verkoop).uitleg}
                    </div>
                  </div>
                </div>
                
                {/* Uitverkoop berekening */}
                {calculationData.uitverkoop_berekening && calculationData.voorraad && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#fff3cd', borderRadius: '8px', border: '2px solid #ffc107' }}>
                    <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>Uitverkoop Berekening</h4>
                    <div style={{ marginTop: '0.5rem' }}>
                      <div>Totaal voorraad: <strong>{calculationData.voorraad.totaal_voorraad} stuks</strong></div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <strong>Dagen tot uitverkoop:</strong>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                          {calculationData.uitverkoop_berekening.berekening_uitverkoop}
                        </div>
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        <strong>Dagen tot bestelmoment:</strong>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                          {calculationData.uitverkoop_berekening.berekening_bestelmoment}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem', lineHeight: 1.45 }}>
                          <strong>Levertijd gebruikt:</strong> {calculationData.product?.levertijd_dagen ?? '-'} dagen
                          {calculationData.product?.levertijd_bron === 'default' && (
 <> — <em>standaard 7 dagen</em> omdat bij deze leverancier nog geen levertijd in het systeem staat. Stel die in bij Leveranciers of Parameters (of een productoverride bij het product).</>
                          )}
                          {calculationData.product?.levertijd_bron === 'leverancier' && calculationData.product?.levertijd_leverancier_dagen != null && (
                            <> — van leverancierskaart ({calculationData.product.levertijd_leverancier_dagen} dagen)</>
                          )}
                          {calculationData.product?.levertijd_bron === 'product' && calculationData.product?.levertijd_product_override != null && (
                            <> — productoverride ({calculationData.product.levertijd_product_override} dagen)</>
                          )}
                        </div>
                      </div>
                      <div style={{ marginTop: '0.5rem' }}>
                        Status: <strong style={{ color: calculationData.uitverkoop_berekening.status === 'kritiek' ? '#ff4444' : '#333' }}>
                          {calculationData.uitverkoop_berekening.status}
                        </strong>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Oude structuur (voor inkoopvoorstellen)
              <>
                {calculationData.product && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem' }}>Voorraad</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                      <div>Huidige voorraad: <strong>{calculationData.product.voorraad}</strong></div>
                      <div>Onderweg: <strong>{calculationData.product.voorraad_onderweg}</strong></div>
                      {(calculationData.product.voorraad_onderweg_kolom != null &&
                        calculationData.product.voorraad_onderweg_kolom !== calculationData.product.voorraad_onderweg_uit_orders) && (
                        <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: '#b45309' }}>
                          DB-kolom had {calculationData.product.voorraad_onderweg_kolom}; geteld uit inkooporders: {calculationData.product.voorraad_onderweg_uit_orders}
                        </div>
                      )}
                      <div>In Productie: <strong>{calculationData.product.in_productie || 0}</strong></div>
                      <div>Totaal: <strong>{calculationData.product.totaal_voorraad}</strong></div>
                      <div>Minimum: <strong>{calculationData.product.minimum_voorraad}</strong></div>
                    </div>
                    {(calculationData.product.in_productie || 0) > 0 && (
                      <div style={{ marginTop: '0.75rem' }}>
                        <button type="button" className="btn btn-small btn-secondary" disabled={correctingInProductie} onClick={handleZetInProductieOpNul}>
                          {correctingInProductie ? 'Bezig…' : "Zet 'In productie' op 0 (correctie)"}
                        </button>
                        <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '0.5rem' }}>Als er feitelijk niets in productie staat</span>
                      </div>
                    )}
                  </div>
                )}

                {calculationData.historische_data && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem' }}>Historische Data</h4>
                    <div>
                      <div>Totaal verkocht (365 dagen): <strong>{calculationData.historische_data.totaal_verkocht_365_dagen} stuks</strong></div>
                      <div>Gemiddeld per maand: <strong>{calculationData.historische_data.gemiddeld_per_maand} stuks</strong></div>
                    </div>
                  </div>
                )}

                {calculationData.berekening && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f5f5f5', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '0.5rem' }}>Berekening</h4>
                    <div style={{ lineHeight: '1.8' }}>
                      <div>Basis verkoop (zonder groei/seizoen): <strong>{calculationData.berekening.verwachte_verkoop_basis} stuks/maand</strong></div>
                      <div>Seizoensfactor (huidige maand): <strong>{calculationData.berekening.seizoensfactor?.toFixed(2) || '-'}</strong></div>
                      {calculationData.berekening.seizoensfactor_gemiddeld_periode != null && (
                        <div>Seizoensfactor gemiddeld (vooruit): <strong>{calculationData.berekening.seizoensfactor_gemiddeld_periode?.toFixed(2)}</strong></div>
                      )}
                      <div>Verwachte verkoop (equivalent): <strong>{typeof calculationData.berekening.verwachte_verkoop_met_seizoen === 'number' ? calculationData.berekening.verwachte_verkoop_met_seizoen.toFixed(2) : calculationData.berekening.verwachte_verkoop_met_seizoen} stuks/maand</strong></div>
                      <div>Dagelijkse verkoop (gem.): <strong>{calculationData.berekening.dagelijkse_verkoop?.toFixed(4) || '-'} stuks/dag</strong></div>
                      <div>Levertijd: <strong>{calculationData.product?.levertijd_dagen || '-'} dagen</strong></div>
                      <div>Buffer dagen: <strong>{calculationData.config?.buffer_dagen || '-'} dagen</strong></div>
                      <div>Dagen vooruit: <strong>{calculationData.berekening.dagen_vooruit} dagen</strong></div>
                      {calculationData.berekening.verkoop_tijdens_levertijd != null && (
                        <div>Verkoop tijdens levertijd: <strong>{calculationData.berekening.verkoop_tijdens_levertijd} stuks</strong></div>
                      )}
                      {calculationData.berekening.aankomst_datum && (
                        <div>Verwachte binnenkomst: <strong>{calculationData.berekening.aankomst_datum}</strong></div>
                      )}
                      {calculationData.berekening.verkoop_na_binnenkomst != null && (
                        <div>Verkoop buffer ná binnenkomst: <strong>{calculationData.berekening.verkoop_na_binnenkomst} stuks</strong></div>
                      )}
                      {calculationData.berekening.voorraad_bij_binnenkomst != null && (
                        <div>Voorraad bij binnenkomst (projectie): <strong>{calculationData.berekening.voorraad_bij_binnenkomst} stuks</strong></div>
                      )}
                      <div>Benodigde voorraad: <strong>{calculationData.berekening.benodigde_voorraad} stuks</strong></div>
                      <div>Minimum met buffer: <strong>{calculationData.berekening.minimum_met_buffer} stuks</strong></div>
                    </div>
                  </div>
                )}

            {calculationData.resultaat && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: calculationData.resultaat.moet_bestellen ? '#fff3cd' : '#d4edda', borderRadius: '8px', border: `2px solid ${calculationData.resultaat.moet_bestellen ? '#ffc107' : '#28a745'}` }}>
                <h4 style={{ marginBottom: '0.5rem' }}>Resultaat</h4>
                <div style={{ lineHeight: '1.8', fontSize: '1.1rem' }}>
                  <div><strong>{calculationData.resultaat.reden}</strong></div>
                  {calculationData.resultaat.moet_bestellen && (
                    <div style={{ marginTop: '0.5rem' }}>
                      Voorgesteld aantal: <strong>{calculationData.resultaat.voorgesteld_aantal} stuks</strong>
                      {calculationData.resultaat.uitleg_voorgesteld && (
                        <div style={{ marginTop: '0.35rem', fontSize: '0.9rem', color: '#555', fontFamily: 'monospace' }}>
                          {calculationData.resultaat.uitleg_voorgesteld}
                        </div>
                      )}
                      {calculationData.resultaat.uitleg_min_inkoop && (
                        <div style={{ marginTop: '0.35rem', fontSize: '0.9rem', color: '#555' }}>
                          {calculationData.resultaat.uitleg_min_inkoop}
                        </div>
                      )}
                    </div>
                  )}
                  {calculationData.resultaat.waarschuwing_voorstel_overzicht && (
                    <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'rgba(0,0,0,0.05)', borderRadius: '6px', fontSize: '0.9rem', color: '#555' }}>
                      {calculationData.resultaat.waarschuwing_voorstel_overzicht}
                      {calculationData.leverancier_minimum && (calculationData.leverancier_minimum.minimale_cbm_per_order > 0 || calculationData.leverancier_minimum.minimale_inkoop_per_order > 0) && (
                        <div style={{ marginTop: '0.35rem' }}>
                          Leverancier-minimum: {calculationData.leverancier_minimum.minimale_cbm_per_order > 0 && <span>min. {calculationData.leverancier_minimum.minimale_cbm_per_order} CBM</span>}
                          {calculationData.leverancier_minimum.minimale_cbm_per_order > 0 && calculationData.leverancier_minimum.minimale_inkoop_per_order > 0 && ' · '}
                          {calculationData.leverancier_minimum.minimale_inkoop_per_order > 0 && <span>min. {calculationData.leverancier_minimum.minimale_inkoop_per_order} stuks</span>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {calculationData.config && (
              <div style={{ padding: '1rem', background: '#e7f3ff', borderRadius: '8px' }}>
                <h4 style={{ marginBottom: '0.5rem' }}>Configuratie</h4>
                <div style={{ lineHeight: '1.8' }}>
                  <div>Verkoopgroei: <strong>{calculationData.config.verkoopgroei * 100}%</strong></div>
                  <div>Buffer dagen: <strong>{calculationData.config.buffer_dagen} dagen</strong></div>
                  <div>Minimum buffer dagen: <strong>{calculationData.config.minimum_buffer_dagen} dagen</strong></div>
                  <div>Buffer percentage: <strong>{calculationData.config.buffer_percentage}%</strong></div>
                  <div>Max buffer stuks: <strong>{calculationData.config.max_buffer_stuks} stuks</strong></div>
                </div>
              </div>
            )}
              </>
            )}
          </div>
        ) : (
          <div>Geen data beschikbaar</div>
        )}
      </Modal>

      {/* Inkooporders Tab */}
      {showInkooporders && (
        <div className="proposals-container inkooporders-tab">
          <div className="inkooporders-tab-header">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Inkooporders</h2>
            <button className="btn btn-primary" onClick={() => { resetInkooporderForm(); setShowInkooporderModal(true); }}>
              <FiPlus /> Nieuwe Order
            </button>
          </div>

          {searchTerm && (
            <div className="search-results-info" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f0fffe', borderRadius: '8px', border: '1px solid #3FDFCF' }}>
              <strong>{filteredInkooporders.length}</strong> van <strong>{inkooporders.length}</strong> order(s) gevonden voor "{searchTerm}"
            </div>
          )}

          <div className="inkooporders-mobile-list">
            {filteredInkooporders.length === 0 ? (
              <div className="empty-state">
                {searchTerm ? `Geen inkooporders gevonden voor "${searchTerm}"` : 'Geen inkooporders gevonden'}
              </div>
            ) : (
              filteredInkooporders.map((order) => {
                const status = getStatusBadge(order.status);
                return (
                  <div key={`mobile-order-${order.id}`} className="mobile-data-card">
                    <div className="mobile-data-card-top">
                      <div>
                        <h4>{order.ordernummer}</h4>
                        <p>{order.leverancier_naam || '-'}</p>
                      </div>
                      <span className={`status-badge ${status.class}`}>{status.label}</span>
                    </div>
                    <div className="mobile-data-grid">
                      <div><span>Datum</span><strong>{formatDateNl(order.datum)}</strong></div>
                      <div><span>Verwacht</span><strong>{formatDateNl(order.verwachte_leverdatum)}</strong></div>
                      <div><span>ATA</span><strong>{formatDateNl(order.ata_tijd)}</strong></div>
                      <div><span>Totaal</span><strong>{formatCurrency(order.totaal_bedrag, order.leverancier_valuta)}</strong></div>
                    </div>
                    <div className="mobile-data-card-actions">
                      {renderOrderActionButtons(order, true)}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="table-container inkooporders-desktop-table">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ordernummer</th>
                  <th>Leverancier</th>
                  <th>Datum</th>
                  <th>Verwachte Leverdatum</th>
                  <th>ATA Tijd</th>
                  <th>Totaal</th>
                  <th>Status</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {filteredInkooporders.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-state">
                      {searchTerm ? `Geen inkooporders gevonden voor "${searchTerm}"` : 'Geen inkooporders gevonden'}
                    </td>
                  </tr>
                ) : (
                  filteredInkooporders.map((order) => {
                    const status = getStatusBadge(order.status);
                    return (
                      <tr key={order.id}>
                        <td 
                          onClick={() => setEditingOrdernummer({ orderId: order.id, value: order.ordernummer || '' })}
                          style={{ cursor: 'pointer', position: 'relative' }}
                          title="Klik om te bewerken"
                        >
                          {editingOrdernummer?.orderId === order.id ? (
                            <input
                              type="text"
                              value={editingOrdernummer.value}
                              onChange={(e) => setEditingOrdernummer({ ...editingOrdernummer, value: e.target.value })}
                              onBlur={() => {
                                if (editingOrdernummer.value.trim() !== (order.ordernummer || '').trim()) {
                                  handleUpdateOrdernummer(order.id, editingOrdernummer.value);
                                } else {
                                  setEditingOrdernummer(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                } else if (e.key === 'Escape') {
                                  setEditingOrdernummer(null);
                                }
                              }}
                              autoFocus
                              style={{ width: '100%', padding: '2px 4px', border: '1px solid #3FDFCF', borderRadius: '4px', fontWeight: 'bold' }}
                            />
                          ) : (
                            <strong>{order.ordernummer}</strong>
                          )}
                        </td>
                        <td>{order.leverancier_naam || '-'}</td>
                        <td>{new Date(order.datum).toLocaleDateString('nl-NL')}</td>
                        <td>{order.verwachte_leverdatum ? new Date(order.verwachte_leverdatum).toLocaleDateString('nl-NL') : '-'}</td>
                        <td>{order.ata_tijd ? new Date(order.ata_tijd).toLocaleDateString('nl-NL') : '-'}</td>
                        <td>{formatCurrency(order.totaal_bedrag, order.leverancier_valuta)}</td>
                        <td>
                          <span className={`status-badge ${status.class}`}>{status.label}</span>
                        </td>
                        <td>
                          {renderOrderActionButtons(order)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Create/Edit Inkooporder Modal */}
          <Modal show={showInkooporderModal} onClose={() => { setShowInkooporderModal(false); resetInkooporderForm(); }} title={editingOrderId ? 'Order Bewerken' : 'Nieuwe Inkooporder'}>
            <form onSubmit={handleInkooporderSubmit} className="form">
              <div className="form-group">
                <label>Leverancier *</label>
                <select
                  value={inkooporderFormData.leverancier_id}
                  onChange={(e) => setInkooporderFormData({ ...inkooporderFormData, leverancier_id: e.target.value })}
                  required
                >
                  <option value="">Selecteer leverancier</option>
                  {leveranciers.map((lev) => (
                    <option key={lev.id} value={lev.id}>{lev.naam}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Verwachte Leverdatum</label>
                <input
                  type="date"
                  value={inkooporderFormData.verwachte_leverdatum}
                  onChange={(e) => setInkooporderFormData({ ...inkooporderFormData, verwachte_leverdatum: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>ATA Tijd (Verwachte Aankomsttijd)</label>
                <input
                  type="datetime-local"
                  value={inkooporderFormData.ata_tijd}
                  onChange={(e) => setInkooporderFormData({ ...inkooporderFormData, ata_tijd: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Notitie voor leverancier</label>
                <textarea
                  value={inkooporderFormData.opmerkingen}
                  onChange={(e) => setInkooporderFormData({ ...inkooporderFormData, opmerkingen: e.target.value })}
                  placeholder="Komt op de inkooporder PDF"
                  rows="2"
                />
              </div>
              <div className="form-group">
                <label>Interne notitie</label>
                <textarea
                  value={inkooporderFormData.interne_notitie}
                  onChange={(e) => setInkooporderFormData({ ...inkooporderFormData, interne_notitie: e.target.value })}
                  placeholder="Alleen intern zichtbaar, niet op PDF"
                  rows="2"
                />
              </div>

              <div className="order-regels">
                <div className="regels-header">
                  <h3>Producten</h3>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addInkooporderRegel}>
                    <FiPlus /> Product Toevoegen
                  </button>
                </div>

                {inkooporderFormData.regels.map((regel, index) => {
                  const suggestion = regel.ean ? (productSuggestions[normalizeEAN(regel.ean)] || productSuggestions[regel.ean]) : null;
                  return (
                    <div key={index} className="inkooporder-create-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr auto', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'start' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <input
                          type="text"
                          value={regel.ean || ''}
                          onChange={(e) => updateInkooporderRegel(index, 'ean', e.target.value)}
                          placeholder="EAN code"
                          style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        {suggestion && (
                          <span style={{ fontSize: '0.85rem', color: '#3FDFCF', fontWeight: '600' }}>
                            ✓ {suggestion.naam}
                          </span>
                        )}
                        {regel.ean && !suggestion && (
                          <span style={{ fontSize: '0.85rem', color: '#ff6b6b' }}>
                            ⚠ Product niet gevonden
                          </span>
                        )}
                      </div>
                      <select
                        value={regel.product_id}
                        onChange={(e) => updateInkooporderRegel(index, 'product_id', e.target.value)}
                        style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        <option value="">Of selecteer...</option>
                        {producten.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.naam} (€{parseFloat(product.inkoopprijs ?? 0).toFixed(2)})
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={regel.aantal}
                        onChange={(e) => updateInkooporderRegel(index, 'aantal', e.target.value)}
                        placeholder="Aantal"
                        required
                        style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={regel.eenheidsprijs}
                        onChange={(e) => updateInkooporderRegel(index, 'eenheidsprijs', e.target.value)}
                        placeholder="Inkoopprijs"
                        required
                        style={{ padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                      <span className="regel-totaal" style={{ padding: '0.5rem', fontWeight: '600', color: '#3FDFCF' }}>
                        {formatCurrency(regel.aantal * regel.eenheidsprijs, leveranciers.find(l => String(l.id) === String(inkooporderFormData.leverancier_id))?.valuta || 'EUR')}
                      </span>
                      <button type="button" className="btn-icon btn-danger" onClick={() => removeInkooporderRegel(index)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  );
                })}

                {inkooporderFormData.regels.length > 0 && (
                  <div className="order-totaal">
                    <strong>Totaal: {formatCurrency(calculateInkooporderTotal(), leveranciers.find(l => String(l.id) === String(inkooporderFormData.leverancier_id))?.valuta || 'EUR')}</strong>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowInkooporderModal(false); resetInkooporderForm(); }}>
                  Annuleren
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingOrderId ? 'Order Bijwerken' : 'Order Aanmaken'}
                </button>
              </div>
            </form>
          </Modal>

          {/* Inkooporder Detail Modal */}
          <Modal show={showInkooporderDetailModal} onClose={() => setShowInkooporderDetailModal(false)} title={`Order ${selectedOrder?.ordernummer}`}>
            {selectedOrder && (
              <div className="order-detail">
                <div className="detail-section">
                  <div className="order-detail-info-layout">
                    <div className="order-detail-info-main">
                      <h3>Order Informatie</h3>
                      {!detailEditMode ? (
                    <>
                      <div className="status-change-container">
                        <label className="status-change-label">Wijzig status:</label>
                        <select
                          value={selectedOrder.status}
                          onChange={(e) => handleOrderStatusChange(selectedOrder.id, e.target.value)}
                          className="status-select status-select-prominent"
                        >
                          <option value="concept">Nieuw voorstel</option>
                          <option value="in_productie">In productie</option>
                          <option value="onderweg">Onderweg</option>
                          <option value="geleverd">Geleverd</option>
                        </select>
                      </div>
                      <p><strong>Leverancier:</strong> {selectedOrder.leverancier_naam}</p>
                      <p><strong>Datum:</strong> {selectedOrder.datum ? new Date(selectedOrder.datum).toLocaleDateString('nl-NL') : '-'}</p>
                      <div className="levertijden-inline" style={{ marginTop: '0.75rem' }}>
                        <div style={{ marginBottom: '0.5rem' }}><strong>Levertijden</strong></div>
                        <div className="levertijden-inline-fields" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.85rem' }}>Verwachte levertijd</label>
                            <input
                              type="date"
                              value={levertijdenInline.verwachte_leverdatum}
                              onChange={(e) => setLevertijdenInline(prev => ({ ...prev, verwachte_leverdatum: e.target.value }))}
                              style={{ padding: '0.4rem 0.5rem' }}
                            />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={{ fontSize: '0.85rem' }}>ATA tijd</label>
                            <input
                              type="datetime-local"
                              value={levertijdenInline.ata_tijd}
                              onChange={(e) => setLevertijdenInline(prev => ({ ...prev, ata_tijd: e.target.value }))}
                              style={{ padding: '0.4rem 0.5rem' }}
                            />
                          </div>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={saveLevertijden}
                            disabled={levertijdenSaving}
                          >
                            {levertijdenSaving ? 'Opslaan…' : 'Levertijden opslaan'}
                          </button>
                        </div>
                      </div>
                      <p><strong>Status:</strong> <span className={`status-badge ${getStatusBadge(selectedOrder.status).class}`}>{getStatusBadge(selectedOrder.status).label}</span></p>
                      <p>
                        <strong>Inkoopnummer:</strong>{' '}
                        {editingOrdernummer?.orderId === selectedOrder.id ? (
                          <input
                            type="text"
                            value={editingOrdernummer.value}
                            onChange={(e) => setEditingOrdernummer({ ...editingOrdernummer, value: e.target.value })}
                            onBlur={() => {
                              if (editingOrdernummer.value.trim() !== (selectedOrder.ordernummer || '').trim()) {
                                handleUpdateOrdernummer(selectedOrder.id, editingOrdernummer.value);
                              } else {
                                setEditingOrdernummer(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              } else if (e.key === 'Escape') {
                                setEditingOrdernummer(null);
                              }
                            }}
                            autoFocus
                            style={{ marginLeft: '0.5rem', minWidth: '180px', maxWidth: '100%', padding: '0.35rem 0.5rem', border: '1px solid #3FDFCF', borderRadius: '6px', fontWeight: '600' }}
                          />
                        ) : (
                          <button
                            type="button"
                            className="btn-link-inline"
                            onClick={() => setEditingOrdernummer({ orderId: selectedOrder.id, value: selectedOrder.ordernummer || '' })}
                            title="Klik om inkoopnummer te bewerken"
                          >
                            {selectedOrder.ordernummer || '-'}
                          </button>
                        )}
                      </p>
                      {selectedOrder.opmerkingen && <p><strong>Notitie voor leverancier:</strong> {selectedOrder.opmerkingen}</p>}
                      {selectedOrder.interne_notitie && <p><strong>Interne notitie:</strong> {selectedOrder.interne_notitie}</p>}
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <label>Inkoopnummer *</label>
                        <input
                          type="text"
                          value={detailForm?.ordernummer || ''}
                          onChange={(e) => setDetailForm({ ...detailForm, ordernummer: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Leverancier *</label>
                        <select
                          value={detailForm?.leverancier_id || ''}
                          onChange={(e) => setDetailForm({ ...detailForm, leverancier_id: e.target.value })}
                        >
                          <option value="">Selecteer leverancier</option>
                          {leveranciers.map((lev) => (
                            <option key={lev.id} value={lev.id}>{lev.naam}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ marginTop: '1rem' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem' }}>Levertijden</h4>
                        <div className="detail-levertijden-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                          <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                            <label>Verwachte levertijd</label>
                            <input
                              type="date"
                              value={detailForm?.verwachte_leverdatum || ''}
                              onChange={(e) => setDetailForm({ ...detailForm, verwachte_leverdatum: e.target.value })}
                            />
                          </div>
                          <div className="form-group" style={{ flex: '1 1 200px', marginBottom: 0 }}>
                            <label>ATA tijd (verwachte aankomst)</label>
                            <input
                              type="datetime-local"
                              value={detailForm?.ata_tijd || ''}
                              onChange={(e) => setDetailForm({ ...detailForm, ata_tijd: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Status</label>
                        <select
                          value={detailForm?.status || 'concept'}
                          onChange={(e) => setDetailForm({ ...detailForm, status: e.target.value })}
                        >
                          <option value="concept">Nieuw voorstel</option>
                          <option value="in_productie">In productie</option>
                          <option value="onderweg">Onderweg</option>
                          <option value="geleverd">Geleverd</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Notitie voor leverancier</label>
                        <textarea
                          rows="2"
                          value={detailForm?.opmerkingen || ''}
                          onChange={(e) => setDetailForm({ ...detailForm, opmerkingen: e.target.value })}
                          placeholder="Komt op de inkooporder PDF"
                        />
                      </div>
                      <div className="form-group">
                        <label>Interne notitie</label>
                        <textarea
                          rows="2"
                          value={detailForm?.interne_notitie || ''}
                          onChange={(e) => setDetailForm({ ...detailForm, interne_notitie: e.target.value })}
                          placeholder="Alleen intern zichtbaar, niet op PDF"
                        />
                      </div>
                    </>
                  )}
                    </div>
                    <div className="order-detail-aside">
                      <InkooporderBetaalinfoCard
                        order={selectedOrder}
                        onOrderRefresh={(data) => {
                          setSelectedOrder(data);
                          fetchInkooporders();
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ width: '100%' }}
                        onClick={() => handleDownloadOrderPdf(selectedOrder)}
                      >
                        <FiDownload /> Download PDF
                      </button>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Producten</h3>
                  {!detailEditMode ? (
                    <>
                    <div className="order-detail-mobile-list">
                      {selectedOrder.regels?.map((regel, index) => {
                        const product = productById[regel.product_id] || productBySku[regel.sku];
                        const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                        const cbmTotaal = (regel.aantal || 0) * cbmPerStuk;
                        return (
                          <div key={`detail-mobile-${index}`} className="mobile-data-card">
                            <div className="mobile-data-card-top">
                              <div>
                                <h4><ProductNaamMetKlasse productId={regel.product_id} naam={regel.product_naam} /></h4>
                                <p>{regel.sku || '-'}</p>
                              </div>
                            </div>
                            <div className="mobile-data-grid">
                              <div><span>Aantal</span><strong>{regel.aantal}</strong></div>
                              <div><span>Inkoopprijs</span><strong>{formatCurrency(getRegelEffectivePrice(regel), selectedOrder?.leverancier_valuta)}</strong></div>
                              <div><span>CBM per stuk</span><strong>{cbmPerStuk > 0 ? cbmPerStuk.toFixed(3) : '-'}</strong></div>
                              <div><span>CBM totaal</span><strong>{cbmTotaal > 0 ? cbmTotaal.toFixed(3) : '-'}</strong></div>
                              <div><span>Totaal</span><strong>{formatCurrency((regel.aantal || 0) * getRegelEffectivePrice(regel), selectedOrder?.leverancier_valuta)}</strong></div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="mobile-data-card">
                        <div className="mobile-pill-value">
                          Totaal CBM: <strong>{selectedOrder.regels?.reduce((sum, regel) => {
                            const product = productById[regel.product_id] || productBySku[regel.sku];
                            const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                            return sum + ((regel.aantal || 0) * cbmPerStuk);
                          }, 0).toFixed(3)}</strong>
                        </div>
                        <div className="mobile-pill-value">
                          Totaal: <strong>{formatCurrency(selectedOrder.regels?.reduce((s, r) => s + ((r.aantal || 0) * getRegelEffectivePrice(r)), 0), selectedOrder?.leverancier_valuta)}</strong>
                        </div>
                      </div>
                    </div>
                    <table className="detail-table order-detail-desktop-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>Aantal</th>
                          <th>Inkoopprijs</th>
                          <th>CBM per stuk</th>
                          <th>CBM totaal</th>
                          <th>Totaal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.regels?.map((regel, index) => {
                          const product = productById[regel.product_id] || productBySku[regel.sku];
                          const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                          const cbmTotaal = (regel.aantal || 0) * cbmPerStuk;
                          return (
                            <tr key={index}>
                              <td><ProductNaamMetKlasse productId={regel.product_id} naam={regel.product_naam} /></td>
                              <td>{regel.sku || '-'}</td>
                              <td>{regel.aantal}</td>
                              <td>{formatCurrency(getRegelEffectivePrice(regel), selectedOrder?.leverancier_valuta)}</td>
                              <td>{cbmPerStuk > 0 ? cbmPerStuk.toFixed(3) : '-'}</td>
                              <td>{cbmTotaal > 0 ? cbmTotaal.toFixed(3) : '-'}</td>
                              <td>{formatCurrency((regel.aantal || 0) * getRegelEffectivePrice(regel), selectedOrder?.leverancier_valuta)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="4"><strong>Totaal</strong></td>
                          <td></td>
                          <td><strong>
                            {selectedOrder.regels?.reduce((sum, regel) => {
                              const product = productById[regel.product_id] || productBySku[regel.sku];
                              const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                              return sum + ((regel.aantal || 0) * cbmPerStuk);
                            }, 0).toFixed(3)}
                          </strong></td>
                          <td><strong>{formatCurrency(selectedOrder.regels?.reduce((s, r) => s + ((r.aantal || 0) * getRegelEffectivePrice(r)), 0), selectedOrder?.leverancier_valuta)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                    </>
                  ) : (
                    <>
                      {detailForm?.regels?.map((regel, index) => {
                        const product = productById[regel.product_id];
                        const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                        const cbmTotaal = (regel.aantal || 0) * cbmPerStuk;
                        const suggestion = regel.ean ? (productSuggestions[normalizeEAN(regel.ean)] || productSuggestions[regel.ean]) : null;
                        return (
                          <div key={index} className="regel-row">
                            <div>
                              <input
                                type="text"
                                value={regel.ean || ''}
                                onChange={(e) => updateDetailRegel(index, 'ean', e.target.value)}
                                placeholder="EAN code"
                              />
                              {suggestion && (
                                <span style={{ fontSize: '0.8rem', color: '#3FDFCF', fontWeight: '600', marginTop: '0.25rem' }}>
                                  ✓ {suggestion.naam}
                                </span>
                              )}
                            </div>
                            <select
                              value={regel.product_id}
                              onChange={(e) => updateDetailRegel(index, 'product_id', e.target.value)}
                              required
                            >
                              <option value="">Selecteer product</option>
                              {producten.map((product) => (
                                <option key={product.id} value={product.id}>
                                  {product.naam} (€{parseFloat(product.inkoopprijs ?? 0).toFixed(2)})
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              min="1"
                              value={regel.aantal}
                              onChange={(e) => updateDetailRegel(index, 'aantal', e.target.value)}
                              placeholder="Aantal"
                              required
                            />
                            <input
                              type="number"
                              step="0.01"
                              value={regel.eenheidsprijs}
                              onChange={(e) => updateDetailRegel(index, 'eenheidsprijs', e.target.value)}
                              placeholder="Inkoopprijs"
                              required
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                              <span style={{ fontWeight: '600', color: '#3FDFCF' }}>
                                CBM: {cbmPerStuk > 0 ? cbmPerStuk.toFixed(3) : '-'}
                              </span>
                              <span style={{ fontSize: '0.8rem' }}>
                                Totaal: {cbmTotaal > 0 ? cbmTotaal.toFixed(3) : '-'}
                              </span>
                            </div>
                            <span className="regel-totaal">
                              {formatCurrency(regel.aantal * regel.eenheidsprijs, selectedOrder?.leverancier_valuta || leveranciers.find(l => String(l.id) === String(detailForm?.leverancier_id))?.valuta || 'EUR')}
                            </span>
                            <button type="button" className="btn-icon btn-danger" onClick={() => removeDetailRegel(index)}>
                              <FiTrash2 />
                            </button>
                          </div>
                        );
                      })}
                      <button type="button" className="btn btn-secondary btn-sm" onClick={addDetailRegel} style={{ marginTop: '0.75rem' }}>
                        <FiPlus /> Regel toevoegen
                      </button>
                      {detailForm?.regels?.length > 0 && (
                        <div className="order-totaal" style={{ marginTop: '0.75rem' }}>
                          <strong>
                            Totaal CBM: {detailForm.regels.reduce((sum, regel) => {
                              const product = productById[regel.product_id];
                              const cbmPerStuk = regel.cbm_per_stuk || (product?.cbm_per_stuk ? parseFloat(product.cbm_per_stuk) : 0);
                              return sum + ((regel.aantal || 0) * cbmPerStuk);
                            }, 0).toFixed(3)}
                          </strong>
                          <br />
                          <strong>
                            Totaal: {formatCurrency(detailForm.regels.reduce((sum, r) => sum + (r.aantal * r.eenheidsprijs), 0), selectedOrder?.leverancier_valuta || leveranciers.find(l => String(l.id) === String(detailForm?.leverancier_id))?.valuta || 'EUR')}
                          </strong>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="detail-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  {!detailEditMode ? (
                    <button className="btn btn-primary" onClick={() => setDetailEditMode(true)}>
                      Bewerk order
                    </button>
                  ) : (
                    <>
                      <button className="btn btn-secondary" onClick={() => { setDetailEditMode(false); setDetailForm(null); handleViewOrderDetail(selectedOrder.id); }}>
                        Annuleren
                      </button>
                      <button className="btn btn-primary" onClick={saveDetailChanges}>
                        Opslaan
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </Modal>
        </div>
      )}
    </div>
  );
};

export default Inkoopvoorstellen;
